import { useState, useRef, useMemo, useEffect } from "react";
import { ref, onValue, get } from "firebase/database";
import { db } from "./firebase";
import { BACKDROPS, BACKDROP_RARITY, useStageRect, previewBackdrop, CAN_HOVER } from "./RoomBackdrop"; // D10 — the try-on channel
import NpcStage from "./NpcStage"; // Venus on the stage with you (caP'n, 2026-09-23)
import { unlockBackdrop, equipBackdrop } from "./progression";
import { unlockDrawPulse, equipDrawPulse, unlockCoinSkin, equipCoinSkin } from "./progression";
import { shelfOrder as coinShelfOrder, SKIN_RARITY_META, DEFAULT_SKIN as DEFAULT_COIN_SKIN, CoinFaces, currencyOf as coinSkinCurrency } from "./coinSkins"; // T11
import { DRAW_PULSES, PULSE_RARITY, DEFAULT_PULSE_ID, DrawPulsePreview } from "./DrawPulse";
import {
  SD, GM, TYPE_COLORS_SD,
  OBSIDIAN_BG, GOLD_METAL, GOLD_METAL_H,
  OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS, OBSIDIAN_PANEL,
  facet, facetTB, SHADOW_RAISED, SHADOW_RECESS,
  textGlow, TEXT_SET, shimmerBorder,
} from "./theme";
import { StarDust } from "./TerrorUI"; // also guarantees the twinkle keyframes are injected
import { BOOSTER_PACKS, FOIL_VAULT, getFoilVaultCost } from "./storyData";   // FEATURED_HOLOS dropped 2026-08-22 with the Limited shelf — see the banner at the shelf body
import StarterArt from "./StarterArt";   // the deck boxes — see StarterArt.js
import { openPack, buyFoilSingle, getPackCardPool, getPackSourceId, STARTER_DECK_COST, starterDeckContents, scrapCopies, scrapValueOf } from "./progression";
import { STARTER_DECKS } from "./starterDecks";
import { ChocolateCoin, DivineCrest, CoinChip, crumbleToCoins } from "./CoinGlyph"; // + T21 scrap chip / D16 crumble / ◆ the Vault's currency
import { CDN, cardUrl } from "./CDN"; // CDN law — one domain, one file (loose-debt sweep 2026-08-18)
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { useModalKeys } from "./KeyboardNav";
import CompanionMenagerie from "./CompanionMenagerie";
// KING'S GRACE (2026-09-16) — the Vault's companion shelf. See GraceVault.jsx.
import GraceVault, { GRACE_VAULT_BLURB } from "./GraceVault";
import { PackArt, TierGem, HOLO_TIER_INFO, TIER_ORDER, getPackSignatureTier, getPackAuraColor } from "./PackArt";
import HoloFoil, { holoGlowStyle } from "./HoloFoil";
import TerrorFrame from "./TerrorFrame";
import { StatGlyph, StatChip } from "./StatGlyph";
import SourceSigil, { primaryOrigin } from "./SourceSigil";
import TheMark from "./TheMark";
import { getCardOrigins } from "./progression";
// ⭐ 2026-08-22 — THE CORE, imported, never retyped. CORE_DRAWERS is the
// single list of which packs are Core, which drawer each sits in, and how
// many cards each drawer deals. The shop used to keep its own copy of the
// first two of those in CATEGORY_DEFS and hardcode the third in two
// places. See the banner on CORE_DRAWERS in progression.js.
import { CORE_DRAWERS, getPackContents } from "./progression";

// ============================================================
// TERROR — BOOSTER PACK / DIVINE SHOP
// GILDED OBSIDIAN RE-FORGE (July 5, 2026)
// ------------------------------------------------------------
// The manuscript boutique became a jewel vault carved from black
// glass. The local vellum-brown GM copy is DEAD — palette imports
// from theme.js v4 (one source of truth; StartMenu/Plaza law).
// Every keyframe moved out of <style> blocks into an imperative
// ensureBoosterKeyframes() — the keyframe-injection trap is closed
// in this file. NEW ambient life: SwirlField (rotating purple/red
// vortex washes + rising crimson embers), amethyst stardust in the
// display case, forged-gold metal frames on the crest counter and
// vault plaque, faceted pressable chrome throughout.
// Behavior, props, handlers, and the mobile column are UNCHANGED.
// ============================================================

// (CDN now imports from CDN.js — the local copy was loose debt; one domain, one file.)

const ALL_CARDS_BP = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const getCard = (id) => ALL_CARDS_BP.find(c => c.id === id) || null;

// ── SHOP MOTION LIBRARY ───────────────────────────────────────
// All per-tier foil CSS lives in ONE place — HoloFoil.js recipes —
// and glows come from holoGlowStyle(tier). What lives here is pure
// shop theater: pack physics, card flips, particles, dust, god-rays,
// and (NEW) the vortex swirls + embers of the Unstable Emporium.
// Injected imperatively to document.head, keyed off node existence —
// never <style> blocks in components (the keyframe trap is dead).
function ensureBoosterKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("bp-keyframes")) return;
  const el = document.createElement("style");
  el.id = "bp-keyframes";
  el.textContent = `
    @keyframes cardFlipIn {
      0%   { transform: rotateY(90deg) scale(0.8); opacity: 0; }
      60%  { transform: rotateY(-8deg) scale(1.05); opacity: 1; }
      100% { transform: rotateY(0deg)  scale(1);    opacity: 1; }
    }
    @keyframes packPulse {
      0%,100% { transform: scale(1);    filter: drop-shadow(0 0 22px var(--pack-glow)); }
      50%     { transform: scale(1.03); filter: drop-shadow(0 0 44px var(--pack-glow)) drop-shadow(0 0 70px var(--pack-glow-2)); }
    }
    @keyframes packCrack {
      0%   { transform: scale(1) rotate(0deg); }
      20%  { transform: scale(1.1) rotate(-2deg); }
      40%  { transform: scale(1.12) rotate(2deg); }
      60%  { transform: scale(1.08) rotate(-1deg); }
      80%  { transform: scale(0.95) rotate(1deg); }
      100% { transform: scale(0) rotate(0deg); opacity: 0; }
    }
    @keyframes particleBurst {
      0%   { transform: translate(0,0) scale(1); opacity: 1; }
      100% { transform: translate(var(--px), var(--py)) scale(0); opacity: 0; }
    }
    @keyframes slideUp {
      from { transform: translateY(40px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    /* ── GOD-RAYS — rotating radiance behind the big holo reveal;
       also drives the shop's slow vortex swirl ── */
    @keyframes bpRays {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes bpRaysRev {
      from { transform: rotate(0deg); }
      to   { transform: rotate(-360deg); }
    }
    /* ── DUST MOTES — boutique air drifting in the caselight ── */
    @keyframes bpMote {
      0%   { transform: translate(0, 0) scale(0.6);        opacity: 0; }
      15%  { opacity: 0.7; }
      50%  { transform: translate(14px, -40px) scale(1);   opacity: 0.45; }
      85%  { opacity: 0.6; }
      100% { transform: translate(-8px, -84px) scale(0.5); opacity: 0; }
    }
    /* ── SEAM GLOW — the tear-here line on the big pack breathes ── */
    @keyframes bpSeam {
      0%,100% { opacity: 0.35; }
      50%     { opacity: 0.9; }
    }
    /* ── CASE FLOAT — packs hover gently on their pedestals ── */
    @keyframes caseFloat {
      0%,100% { transform: translateY(0px); }
      50%     { transform: translateY(-6px); }
    }
    .pack-pedestal { transition: transform 0.22s ease, filter 0.22s ease; }
    .pack-pedestal:hover { transform: translateY(-10px) scale(1.05); }
    /* ── SWIRL WASH — purple/red nebulae breathing through the glass ── */
    @keyframes bpWash {
      0%,100% { opacity: 0.5;  transform: translate(0, 0) scale(1); }
      50%     { opacity: 0.95; transform: translate(3%, -2%) scale(1.08); }
    }
    @keyframes bpWash2 {
      0%,100% { opacity: 0.45; transform: translate(0, 0) scale(1); }
      50%     { opacity: 0.85; transform: translate(-3%, 2%) scale(1.1); }
    }
    /* ── EMBERS — crimson sparks rising off the void floor ── */
    @keyframes bpEmber {
      0%   { transform: translate(0, 0) scale(0.7);            opacity: 0; }
      12%  { opacity: 0.8; }
      55%  { transform: translate(10px, -64px) scale(1);       opacity: 0.5; }
      100% { transform: translate(-6px, -128px) scale(0.35);   opacity: 0; }
    }
    /* ── SHEEN — a light sweep across the gold title rule ── */
    @keyframes bpSheen {
      0%   { transform: translateX(-160%) skewX(-18deg); }
      100% { transform: translateX(260%)  skewX(-18deg); }
    }
    @media (prefers-reduced-motion: reduce) {
      .bp-rays, .bp-mote, .bp-wash, .bp-ember, .bp-sheen { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureBoosterKeyframes();

// ─────────────────────────────────────────────────────────────
// SWIRL FIELD — the Emporium's living air. A slow-turning conic
// vortex (purple/red, heavily blurred so it reads as aurora, not
// geometry), two counter-breathing nebula washes, and crimson
// embers rising off the floor. Absolutely positioned, pointer-
// transparent, cheap: 3 blurred divs + 5 embers.
// ─────────────────────────────────────────────────────────────
function SwirlField({ accent = GM.amethyst, accent2 = "#dc2626", embers = true }) {
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {/* The vortex — a vast conic swirl turning once every 100s */}
      <div className="bp-rays" style={{
        position: "absolute", top: "50%", left: "50%",
        width: "165vmax", height: "165vmax", marginLeft: "-82.5vmax", marginTop: "-82.5vmax",
        background: `conic-gradient(from 0deg,
          transparent 0deg, ${accent}12 42deg, transparent 95deg,
          ${accent2}0d 150deg, transparent 205deg,
          ${accent}15 265deg, transparent 320deg, ${accent2}08 345deg, transparent 360deg)`,
        filter: "blur(26px)",
        animation: "bpRays 100s linear infinite",
      }} />
      {/* Nebula washes — purple breathes in, red breathes out */}
      <div className="bp-wash" style={{
        position: "absolute", top: "6%", left: "-12%", width: "72%", height: "48%",
        background: `radial-gradient(ellipse, ${accent}22 0%, ${accent}0a 45%, transparent 70%)`,
        filter: "blur(26px)", animation: "bpWash 16s ease-in-out infinite",
      }} />
      <div className="bp-wash" style={{
        position: "absolute", bottom: "8%", right: "-14%", width: "64%", height: "44%",
        background: `radial-gradient(ellipse, ${accent2}1c 0%, ${accent2}08 45%, transparent 70%)`,
        filter: "blur(24px)", animation: "bpWash2 21s ease-in-out infinite 4s",
      }} />
      {/* Embers — crimson sparks drifting up through the swirl */}
      {embers && [[16, 88, 9, 0], [44, 94, 12, 3.4], [70, 90, 10, 6.1], [86, 96, 13, 1.8], [30, 98, 11, 8.2]].map(([x, y, dur, del], i) => (
        <div key={i} className="bp-ember" style={{
          position: "absolute", left: `${x}%`, top: `${y}%`,
          width: i % 2 ? 2.5 : 3.5, height: i % 2 ? 2.5 : 3.5, borderRadius: "50%",
          background: i % 3 === 0 ? "#f87171" : "#dc2626",
          boxShadow: `0 0 6px ${i % 3 === 0 ? "#f87171" : "#dc2626"}`,
          opacity: 0,
          animation: `bpEmber ${dur}s ease-out ${del}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ── PACK CARD (the unopened pack) ─────────────────────────────
// The pack you tear open is the SAME wrapped object you bought off
// the shelf — the real PackArt wrapper at ritual size, haloed by its
// signature-tier aura, seam breathing "tear here."
function PackDisplay({ pack, onTap, cracking }) {
  const sigTier = getPackSignatureTier(pack);
  const sigInfo = sigTier ? HOLO_TIER_INFO[sigTier] : null;
  const aura = getPackAuraColor(pack);
  const foil = !!pack.holoGuarantee;

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 12, color: aura, letterSpacing: 4, marginBottom: 18, fontFamily: GM.font, textShadow: `0 0 12px ${aura}88` }}>
        TAP TO OPEN
      </div>

      <div
        onClick={onTap}
        style={{
          "--pack-glow": aura + "77",
          "--pack-glow-2": aura + "33",
          position: "relative",
          width: 216, margin: "0 auto",
          cursor: "pointer",
          animation: cracking ? "packCrack 0.6s ease-in forwards" : "packPulse 2.4s ease-in-out infinite",
        }}
      >
        {/* Signature-tier aura halo behind the wrapper */}
        <div style={{
          position: "absolute", inset: -26, borderRadius: "50%",
          background: `radial-gradient(ellipse at 50% 45%, ${aura}55, ${aura}18 55%, transparent 78%)`,
          filter: "blur(6px)", pointerEvents: "none",
        }} />

        {/* The real wrapped pack, ritual size */}
        <div style={{ position: "relative" }}>
          <PackArt accentColor={aura} code={pack.code} w={216} h={302} foil={foil} premium={!!sigInfo} />

          {/* Breathing seam — "tear here" light along the crimp line */}
          <div style={{
            position: "absolute", top: "17.5%", left: "8%", right: "8%", height: 2,
            background: `linear-gradient(90deg, transparent, ${aura}, #ffffffcc, ${aura}, transparent)`,
            boxShadow: `0 0 12px ${aura}aa`,
            animation: "bpSeam 1.8s ease-in-out infinite",
            pointerEvents: "none",
          }} />

          {/* FOIL ribbon for guaranteed-foil packs */}
          {foil && (
            <div style={{
              position: "absolute", top: 14, left: -8, zIndex: 2,
              background: `linear-gradient(135deg, ${aura}, ${aura}aa)`,
              color: "#0a0005", fontSize: 9, fontWeight: 900, letterSpacing: 1.5,
              padding: "3px 10px", borderRadius: 4, fontFamily: GM.font,
              boxShadow: `0 0 14px ${aura}aa`,
            }}>100% FOIL</div>
          )}
        </div>
      </div>

      {/* Name + signature-tier tag beneath the pack */}
      <div style={{ marginTop: 18, fontSize: 16, fontWeight: 900, fontStyle: "italic", color: aura, letterSpacing: 2, fontFamily: GM.font, textShadow: `0 0 14px ${aura}66, 0 1px 2px #000` }}>
        {pack.name}
      </div>
      {sigInfo && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5, marginTop: 7,
          background: `${sigInfo.color}14`, border: `1px solid ${sigInfo.color}66`,
          borderRadius: 10, padding: "2px 10px",
          boxShadow: `inset 0 1px 0 rgba(226,232,244,0.1)`,
        }}>
          <TierGem color={sigInfo.color} size={13} glow={false} />
          <span style={{ fontSize: 9, fontWeight: 900, color: sigInfo.color, letterSpacing: 1, fontFamily: GM.font }}>
            {sigInfo.label.toUpperCase()} INSIDE
          </span>
        </div>
      )}
      {/* ⚠ A FOURTH HARDCODED "10 CARDS" LIVED HERE, and it was the
          worst of the four: the shelf, the panel and the confirm modal
          are all things you read BEFORE paying, but this is the sealed
          packet in your hand after you have. Found only by opening a
          three-card Tome pack in a browser — no grep for the string
          "10 cards" catches "10 CARDS" on its own line, which is why
          the fix everywhere else is the resolver and not a better
          search. Same call as the plaque, so they cannot disagree. */}
      <div style={{ marginTop: 12, fontSize: 10, color: GM.vellumFaint, letterSpacing: 3, fontFamily: GM.font, textShadow: TEXT_SET }}>
        {getPackContents(pack.id, { boutique: foil }).total} CARDS
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 2, fontFamily: GM.font, textShadow: TEXT_SET }}>
        {pack.flavor}
      </div>
    </div>
  );
}

// ── PARTICLES BURST ───────────────────────────────────────────
function ParticleBurst({ color, active }) {
  if (!active) return null;
  const particles = Array.from({ length: 24 }, (_, i) => {
    const angle = (i / 24) * 360;
    const dist = 60 + Math.random() * 80;
    const px = `${Math.cos(angle * Math.PI / 180) * dist}px`;
    const py = `${Math.sin(angle * Math.PI / 180) * dist}px`;
    return { px, py, size: 3 + Math.random() * 6, delay: Math.random() * 0.3 };
  });

  return (
    <div style={{ position: "absolute", top: "50%", left: "50%", pointerEvents: "none", zIndex: 10 }}>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: "absolute",
          width: p.size, height: p.size,
          borderRadius: "50%",
          background: color,
          "--px": p.px, "--py": p.py,
          animation: `particleBurst 0.8s ease-out ${p.delay}s forwards`,
          top: 0, left: 0,
          boxShadow: `0 0 ${p.size * 2}px ${color}`,
        }} />
      ))}
    </div>
  );
}

// ── REVEALED CARD ─────────────────────────────────────────────
function RevealedCard({ cardResult, index, isRevealed, onClick, scrappedPay = null }) {
  const { cardId, isHolo, holoTier, rarity } = cardResult;
  const card = getCard(cardId);
  const typeColor = card ? (TYPE_COLORS_SD[card.type] || GM.vellumFaint) : GM.vellumFaint;
  const isSpecial = isHolo;
  const tier = holoTier || (isHolo ? "holo_silver" : null);
  const tierInfo = tier ? HOLO_TIER_INFO[tier] : null;
  // Tier-TRUE glow from the single foil source of truth — a Depths pull
  // breathes ocean-blue, a Solrend pull breathes crimson.
  const glow = isSpecial && tier ? holoGlowStyle(tier) : null;
  const holoBorderColor = tierInfo ? tierInfo.color : typeColor;
  const holoLabel = tierInfo ? `${tierInfo.glyph} ${tierInfo.label.toUpperCase()}` : "";

  if (!isRevealed) {
    return (
      <div onClick={onClick} style={{
        width: 100, height: 140, borderRadius: 12, flexShrink: 0,
        cursor: "pointer", transition: "transform 0.2s",
        boxShadow: `0 0 16px ${GM.amethystDim}88, inset 0 1px 0 rgba(226,232,244,0.1)`,
        animation: `cardFlipIn 0.5s ease-out ${index * 0.12}s both`,
        overflow: "hidden", position: "relative",
      }}>
        <img src={`${CDN}/card_back.webp`} alt="card back"
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12, display: "block" }}
          onError={e => { e.target.style.display = "none"; }} />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, border: `1px solid ${GM.pageBorder}` }}>
          <span style={{ color: GM.inkBrown, fontSize: 28, opacity: 0.3 }}>◆</span>
        </div>
      </div>
    );
  }

  // Revealed
  return (
    <div onClick={onClick} style={{ position: "relative", flexShrink: 0, borderRadius: 12, ...(glow || {}) }}>
      <div style={{
        width: 100, height: 140, borderRadius: 12,
        background: tier === 'holo_mythic' ? `linear-gradient(145deg, ${GM.page}, ${GM.pageMid}33)` : tier === 'holo_gold' ? `linear-gradient(145deg, ${GM.page}, #b8860b22)` : tier === 'holo_silver' ? `linear-gradient(145deg, ${GM.page}, #88888822)` : `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`,
        border: `2px solid ${isSpecial ? holoBorderColor : typeColor + "99"}`,
        display: "flex", flexDirection: "column", padding: "7px 8px",
        cursor: "pointer", overflow: "hidden", fontFamily: GM.font,
        gap: 4,
        animation: `cardFlipIn 0.4s ease-out both`,
        transform: "scale(1)",
        transition: "transform 0.2s",
      }}>
        {/* Holo foil overlay — unified HoloFoil (same look everywhere) */}
        {isSpecial && <HoloFoil tier={tier} radius={10} corners={false} />}

        {/* Full card art */}
        <img src={cardUrl(cardId)} alt={card?.name || cardId}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: 10, opacity: 0.9 }}
          onError={e => { e.target.style.display = "none"; }} />
        {/* Dark gradient so text stays readable */}
        <div style={{ position: "absolute", inset: 0, borderRadius: 10, background: "linear-gradient(to bottom, rgba(0,0,5,0.65) 0%, rgba(0,0,5,0.0) 40%, rgba(0,0,5,0.8) 100%)" }} />

      {/* Card content */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
          <div style={{ fontSize: 9, color: "#fff", letterSpacing: 1, fontWeight: 900, lineHeight: 1, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
            {card?.type?.substring(0, 3).toUpperCase()}{card?.level ? ` L${card.level}` : ""}
            {isHolo && <span style={{ color: holoBorderColor, marginLeft: 4 }}>{holoLabel}</span>}
          </div>
          <div style={{ fontSize: 11, color: isSpecial ? GM.inkGoldBright : GM.vellumDim, fontStyle: "italic", lineHeight: 1.3, flex: 1, overflow: "hidden", fontWeight: isSpecial ? 900 : 400, fontFamily: GM.font }}>
            {card?.name || cardId}
          </div>
          {card?.lyt !== undefined && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <StatChip kind="lyt" value={card.lyt} color={SD.red} size={10} fontSize={12} bare />
              <StatChip kind="lyf" value={card.lyf} color={SD.blue} size={10} fontSize={12} bare />
            </div>
          )}
          {card?.khaoCost != null && card?.type !== "Escendant" && (
            <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: GM.inkGold }}><StatGlyph kind="khao" size={8} color={GM.inkGold} />{card.khaoCost}</div>
          )}
          {card?.effectTag && (
            <div style={{ fontSize: 8, color: isSpecial ? GM.inkGold : GM.vellumFaint, fontFamily: GM.font }}>{card.effectTag}</div>
          )}
        </div>
      </div>

      {/* Holo badge — names the exact tier (J'YNXI, SHADOW, DEPTHS, …) */}
      {isHolo && (
        <div style={{
          position: "absolute", top: -8, right: -8,
          background: `linear-gradient(135deg, ${holoBorderColor}, ${GM.inkGoldBright})`,
          color: GM.page, fontSize: 8, fontWeight: 900,
          padding: "3px 7px", borderRadius: 10,
          letterSpacing: 1, fontFamily: GM.font,
          boxShadow: `0 0 12px ${holoBorderColor}`,
          whiteSpace: "nowrap",
        }}>{holoLabel || "HOLO"}</div>
      )}

      {/* T21 — this pull was scrapped at the reveal; the tile wears it. */}
      {scrappedPay != null && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 5, borderRadius: 12,
          background: "rgba(8,6,4,0.74)", backdropFilter: "grayscale(0.85)",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 4,
        }}>
          <span style={{ fontSize: 16, color: "#ff9c4a" }}>♻</span>
          <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: 1.5, color: "#ff9c4a", fontFamily: GM.font }}>SCRAPPED +◕{scrappedPay}</span>
        </div>
      )}
    </div>
  );
}

// ── BIG HOLO REVEAL ───────────────────────────────────────────
// The pull moment is PHYSICAL. The card tilts in 3D under your
// pointer/finger (rotateX/rotateY tracked live), a specular highlight
// slides across the surface where you touch it — exactly how you'd
// angle a real foil under light — and slow god-rays in the tier's own
// color turn behind it. Tap anywhere to continue, same as ever.
function HoloReveal({ cardResult, uid = null, dupeScrap = null, onClose }) {
  const { cardId, isMascot, holoTier, isHolo } = cardResult;
  const card = getCard(cardId);
  // Plain cards are welcome here now (re-tapping a revealed card): no
  // foil, chrome falls back to the card's type color, tilt + provenance
  // stay — every pull gets the full inspection moment.
  const tier = holoTier || (isHolo ? "holo_mythic" : null);
  const tierInfo = tier ? (HOLO_TIER_INFO[tier] || HOLO_TIER_INFO.holo_silver) : null;
  const typeColor = card ? (TYPE_COLORS_SD[card.type] || GM.inkGold) : GM.inkGold;
  const holoBorderColor = tierInfo ? tierInfo.color : typeColor;
  useModalKeys(onClose);
  const holoLabel = tierInfo ? `${tierInfo.glyph} ${tierInfo.label.toUpperCase()} HOLOGRAPHIC ${tierInfo.glyph}` : `◆ ${(card?.type || "CARD").toUpperCase()} ◆`;

  // ── 3D tilt state — the card physically follows the pointer ──
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, px: 50, py: 38, live: false });
  // THE MARK (H18) — folded behind a toggle; the pull moment stays a card.
  const [showMark, setShowMark] = useState(false);

  // ── T21 · scrap right there on the pull — DUPES ONLY ─────────
  // The reveal offers only the SAME-VARIANT dupe case (you already own
  // ≥2 of this exact tier, or ≥2 plain): a labeled, conservative gate —
  // cross-tier "I own the gold, pulled the silver" dupes go through the
  // Forge, the full lane. The pull already wrote to the collection, so
  // the fresh record INCLUDES this copy; ≥2 therefore means "owned one
  // before this pull." Tierless legacy foils (old mascot grants carried
  // no tier) are unpriceable and never offered.
  const scrapTier = isHolo ? (holoTier || null) : null;
  const tierlessFoil = isHolo && !holoTier;
  const [rec, setRec] = useState(null);
  const [coinsNow, setCoinsNow] = useState(null);
  const [scrapBusy, setScrapBusy] = useState(false);
  const [scrapPaid, setScrapPaid] = useState(dupeScrap?.scrappedPay ?? null);
  useEffect(() => {
    if (!uid || !cardId) return;
    let dead = false;
    (async () => {
      try {
        const [cs, bs] = await Promise.all([
          get(ref(db, `profiles/${uid}/collection/${cardId}`)),
          get(ref(db, `profiles/${uid}/chocolateCoins`)),
        ]);
        if (!dead) {
          setRec(cs.exists() ? cs.val() : null);
          setCoinsNow(bs.exists() ? bs.val() : 0);
        }
      } catch (_) { /* non-fatal — scrap option just won't offer */ }
    })();
    return () => { dead = true; };
  }, [uid, cardId]);
  const isDupe = rec ? (scrapTier ? (rec.holoTiers?.[scrapTier] || 0) >= 2 : (rec.count || 0) >= 2) : false;
  const canScrap = !!uid && !tierlessFoil && isDupe && scrapPaid == null;
  const doScrapPull = async (e) => {
    e.stopPropagation();
    if (scrapBusy || !canScrap) return;
    setScrapBusy(true);
    try {
      const res = await scrapCopies(uid, [{ cardId, tier: scrapTier, copies: 1 }]);
      if (res.success) {
        setScrapPaid(res.paid);
        if (res.newBalance != null) setCoinsNow(res.newBalance);
        if (dupeScrap && dupeScrap.onScrapped) dupeScrap.onScrapped(res.paid);
        // D16 — the pull crumbles in your hands: motes into the ◕ chip,
        // the card collapses, and the reveal excuses itself. No toast.
        const el = cardRef.current;
        crumbleToCoins(el, { motes: 20, tierColor: tierInfo ? tierInfo.color : null });
        try {
          el && el.animate(
            [
              { transform: "scale(1) rotate(0deg)", opacity: 1 },
              { transform: "scale(0.05) rotate(9deg)", opacity: 0 },
            ],
            { duration: 620, delay: 160, easing: "cubic-bezier(0.55,0,0.8,0.4)", fill: "forwards" }
          );
        } catch (_) {}
        setTimeout(onClose, 1100);
      }
    } catch (_) { /* non-fatal */ }
    finally { setScrapBusy(false); }
  };

  const handleMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const relX = (e.clientX - r.left) / r.width;   // 0..1
    const relY = (e.clientY - r.top) / r.height;   // 0..1
    setTilt({
      ry: (relX - 0.5) * 22,          // left/right lean
      rx: -(relY - 0.5) * 22,         // top/bottom lean
      px: relX * 100, py: relY * 100, // specular follows the touch
      live: true,
    });
  };
  const handleLeave = () => setTilt({ rx: 0, ry: 0, px: 50, py: 38, live: false });

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,0.97)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(8px)",
      overflow: "hidden",
    }} onClick={onClose}>

      {/* Tier-colored radiance */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 50% 46%, ${holoBorderColor}1e 0%, transparent 62%)`,
        pointerEvents: "none",
      }} />

      {/* God-rays — slow turning radiance behind the card, tier-true */}
      <div style={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <div className="bp-rays" style={{
          width: "140vmin", height: "140vmin", flexShrink: 0,
          background: `repeating-conic-gradient(from 0deg, transparent 0deg 11deg, ${holoBorderColor}14 11deg 15deg, transparent 15deg 24deg)`,
          WebkitMaskImage: "radial-gradient(circle, #000 0%, transparent 62%)",
          maskImage: "radial-gradient(circle, #000 0%, transparent 62%)",
          animation: "bpRays 70s linear infinite",
        }} />
      </div>

      {/* Counter-swirl — a faint amethyst vortex turning the other way,
          so the radiance feels like it's stirring the void */}
      <div style={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <div className="bp-rays" style={{
          width: "120vmin", height: "120vmin", flexShrink: 0,
          background: `conic-gradient(from 90deg, transparent 0deg, ${GM.amethyst}10 60deg, transparent 130deg, ${holoBorderColor}0a 220deg, transparent 300deg)`,
          filter: "blur(22px)",
          animation: "bpRaysRev 55s linear infinite",
        }} />
      </div>

      <div style={{ fontSize: 10, color: holoBorderColor, letterSpacing: 6, marginBottom: 20, fontFamily: GM.font, animation: "slideUp 0.6s ease-out both", textShadow: `0 0 14px ${holoBorderColor}88`, zIndex: 1 }}>
        {isMascot ? "◆ DUNGEON MASCOT ◆" : holoLabel}
      </div>

      {/* Large holo card — 3D tilt stage */}
      <div style={{ perspective: 900, animation: "slideUp 0.5s ease-out 0.1s both", zIndex: 1 }}>
        <div
          ref={cardRef}
          onPointerMove={handleMove}
          onPointerLeave={handleLeave}
          style={{
            position: "relative",
            transform: `rotateX(${tilt.rx.toFixed(2)}deg) rotateY(${tilt.ry.toFixed(2)}deg)`,
            transformStyle: "preserve-3d",
            transition: tilt.live ? "transform 0.06s linear" : "transform 0.5s cubic-bezier(0.2, 0.8, 0.3, 1.1)",
            ...(tier ? holoGlowStyle(tier) : { boxShadow: `0 0 24px ${holoBorderColor}55, 0 0 60px ${holoBorderColor}22` }),
            borderRadius: 18,
          }}
        >
          <div style={{
            width: 210, height: 294, borderRadius: 18,
            background: `linear-gradient(145deg, ${GM.page}, ${GM.pageMid})`,
            border: `3px solid ${holoBorderColor}`,
            display: "flex", flexDirection: "column", padding: "14px 16px",
            overflow: "hidden", fontFamily: GM.font, gap: 8,
            position: "relative",
          }}>
            {/* Full card art */}
            <img src={cardUrl(cardId)} alt={card?.name || cardId}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: 16, opacity: 0.85 }}
              onError={e => { e.target.style.display = "none"; }} />
            {/* Dark gradient overlay */}
            <div style={{ position: "absolute", inset: 0, borderRadius: 16, background: "linear-gradient(to bottom, rgba(0,0,5,0.6) 0%, rgba(0,0,5,0.0) 35%, rgba(0,0,5,0.85) 100%)" }} />
            {/* Foil overlay on top of art — unified HoloFoil, full premier
                treatment (corners on) for the big pull moment. */}
            {tier && <HoloFoil tier={tier} radius={16} corners={true} />}

            {/* ORIGIN sigil — the one-origin law, worn BY the card:
                just the mark, crowning the pull top-center. This pull's
                own stamp when we have it; canonical primary otherwise. */}
            {(() => {
              const src = cardResult.source || primaryOrigin(null, getCardOrigins(cardId));
              if (!src) return null;
              return (
                <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                  <SourceSigil source={src} size={34} />
                </div>
              );
            })()}

            {/* Specular highlight — the light slides where you touch,
                exactly like angling a real foil card under a lamp. */}
            <div style={{
              position: "absolute", inset: 0, borderRadius: 16,
              background: `radial-gradient(circle at ${tilt.px.toFixed(1)}% ${tilt.py.toFixed(1)}%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 24%, transparent 48%)`,
              mixBlendMode: "overlay",
              opacity: tilt.live ? 1 : 0.55,
              transition: "opacity 0.4s ease",
              pointerEvents: "none",
            }} />

            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
              <div style={{ fontSize: 11, color: "#fff", letterSpacing: 2, fontWeight: 900, textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
                {card?.type?.toUpperCase()}{card?.level ? ` • L${card.level}` : ""}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, fontStyle: "italic", color: GM.inkGoldBright, lineHeight: 1.2, textShadow: "0 2px 8px rgba(0,0,0,0.95)" }}>
                {card?.name || cardId}
              </div>
              {card?.lyt !== undefined && (
                <div style={{ display: "flex", gap: 24, marginTop: 8 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: GM.vellumFaint }}>LYT</div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: SD.red, fontFamily: GM.font }}>{card.lyt}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: GM.vellumFaint, fontFamily: GM.font }}>LYF</div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: SD.blue, fontFamily: GM.font }}>{card.lyf}</div>
                  </div>
                </div>
              )}
              {card?.effect && (
                <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.8, marginTop: 4, flex: 1, fontFamily: GM.font }}>
                  {card.effectTag && <span style={{ color: GM.inkGold, fontWeight: 900 }}>{card.effectTag}: </span>}
                  {card.effect.substring(0, 120)}{card.effect.length > 120 ? "…" : ""}
                </div>
              )}
            </div>
          </div>

          {/* Tier badge — names the pull in its own color, bottom-center */}
          {tierInfo && (
            <div style={{
              position: "absolute", bottom: -12, left: "50%", transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              background: `linear-gradient(135deg, ${holoBorderColor}, ${GM.inkGoldBright})`,
              color: GM.page, fontSize: 10, fontWeight: 900,
              padding: "4px 12px", borderRadius: 12,
              letterSpacing: 2, fontFamily: GM.font,
              boxShadow: `0 0 20px ${holoBorderColor}`,
            }}>{tierInfo.glyph} {tierInfo.label.toUpperCase()}</div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 28, fontSize: 13, color: GM.vellumDim, fontStyle: "italic", animation: "slideUp 0.5s ease-out 0.3s both", fontFamily: GM.font, zIndex: 1, textShadow: TEXT_SET }}>
        {card?.name} — added to your collection
      </div>

      {/* ── ORIGIN — the one-origin law now lives ON the card itself
          (sigil top-center); no text strip beneath the pull anymore. */}

      {/* ── THE MARK (H18) — this pull's papers, summonable right at the
          reveal. The record here is THIS COPY's truth (its source stamp +
          the tier just pulled), not the full collection ledger — that
          lives in Codex/Collection. stopPropagation island: tapping the
          toggle or reading the Mark must not dismiss the reveal. */}
      <div onClick={e => e.stopPropagation()} style={{ zIndex: 1, width: "min(88vw, 300px)", animation: "slideUp 0.5s ease-out 0.4s both" }}>
        <button onClick={() => setShowMark(m => !m)} style={{
          width: "100%", marginTop: 16, padding: "8px",
          background: showMark ? `${GM.inkGold}18` : "transparent",
          border: `1px solid ${GM.inkGold}66`,
          borderRadius: 2, cursor: "pointer", color: GM.inkGold,
          fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2.5,
        }}>▦ THE MARK</button>
        {showMark && (
          <div style={{ maxHeight: "38vh", overflowY: "auto", marginTop: 10 }}>
            <TheMark
              being={{ key: cardId, name: card?.name || cardId, type: card?.type, game: "Terror" }}
              record={{
                sources: cardResult.source ? { [cardResult.source]: 1 } : undefined,
                holoTiers: tier ? { [tier]: 1 } : undefined,
              }}
              origins={getCardOrigins(cardId)}
              width="100%"
            />
          </div>
        )}
      </div>

      {/* ── T21 · the scrap option, right on the pull (dupes only).
          stopPropagation island — scrapping must not dismiss the reveal;
          the crumble dismisses it itself. */}
      {canScrap && (
        <div onClick={e => e.stopPropagation()} style={{ zIndex: 1, marginTop: 12, display: "flex", alignItems: "center", gap: 10, animation: "slideUp 0.5s ease-out 0.45s both" }}>
          <button disabled={scrapBusy} onClick={doScrapPull} style={{
            background: "#ff9c4a1c", border: "1px solid #ff9c4a",
            color: "#ff9c4a", borderRadius: 8, padding: "8px 16px",
            cursor: scrapBusy ? "wait" : "pointer", fontFamily: GM.font,
            fontSize: 11, fontWeight: 900, letterSpacing: 2, opacity: scrapBusy ? 0.6 : 1,
          }}>{scrapBusy ? "SCRAPPING…" : `♻ SCRAP THIS PULL · ◕${scrapValueOf(scrapTier)}`}</button>
          <CoinChip amount={coinsNow} />
        </div>
      )}
      {scrapPaid != null && (
        <div onClick={e => e.stopPropagation()} style={{ zIndex: 1, marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, color: "#ff9c4a", fontWeight: 900, letterSpacing: 2, fontFamily: GM.font }}>♻ SCRAPPED · +◕{scrapPaid}</span>
          <CoinChip amount={coinsNow} />
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 11, color: GM.vellumFaint, animation: "slideUp 0.5s ease-out 0.5s both", fontFamily: GM.font, zIndex: 1, textShadow: TEXT_SET }}>
        tilt the card · tap anywhere to continue
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PACK SHOP SCREEN — browse & buy packs
// ─────────────────────────────────────────────────────────────
// ── EQUIPMENT ITEMS ──────────────────────────────────────────
// ⚠ TEST PRICING, 2026-08-19. caP'n: "honestly just make every
// single item in the shop 1 xD". Every non-zero price on this shelf
// is 1 so the whole economy can be walked end to end in an evening.
// A zero stays a zero -- zero means "already yours", not "free".
// THIS IS ONE COMMIT. `git revert` it and the real numbers return.
const EQUIPMENT_ITEMS = [
  { id: "equip_wizards_hat",   name: "Wizard's Hat",       icon: "🎩", cost: 1, description: "A mystical pointed hat. Equip to activate Wizard's Cap.", curio: "curio_wizards_cap" },
  { id: "equip_ring",          name: "Ring of Lyf",        icon: "💍", cost: 1, description: "A simple band of power. Equip to activate Circle of Lyf.", curio: "curio_circle_of_lyf" },
  { id: "equip_jynxi_merch",   name: "J'ynxi Merch",       icon: "🎭", cost: 1, description: "Official Terror merchandise. Equip to activate J'ynxi's Wares.", curio: "curio_jynxi_wares" },
  { id: "equip_spectacles",    name: "Spectral Frames",    icon: "👓", cost: 1, description: "Mysterious eyewear. Equip to activate Spectacular Spectacles.", curio: "curio_spectacles" },
  { id: "equip_necklace",      name: "Amulet Chain",       icon: "📿", cost: 1, description: "An arcane necklace. Equip to activate Mage's Amulet.", curio: "curio_mages_amulet" },
  { id: "equip_elixir",        name: "Elixir Flask",       icon: "🧪", cost: 1, description: "A small vial of mystical brew. Equip to activate Elixir of Health.", curio: "curio_elixir" },
  { id: "equip_robe",          name: "Warlock's Cloak",    icon: "🧥", cost: 1, description: "A flowing outer garment. Equip to activate Warlock's Robe.", curio: "curio_warlocks_robe" },
  { id: "equip_beb_plush",     name: "Beb Plush",          icon: "🧸", cost: 1, description: "A stuffed Beb companion. Equip to activate Beb Buddy.", curio: "curio_beb_buddy" },
  { id: "equip_beb_accessory", name: "Beb Accessory Kit",  icon: "🎀", cost: 1, description: "Tiny wearables for your Beb Plush. Equip to activate Badass Beb.", curio: "curio_badass_beb" },
];
// ⚠ <FeaturedCard> WAS HERE AND IS GONE (2026-08-22). It rendered the
// Limited Edition strip, and caP'n deleted that section along with
// Core, Premier and Chaos. Its two listings survive as Foil Vault
// overrides, so nothing became unbuyable; only the shelf went. Removed
// rather than commented out because a 90-line dead component is how a
// file gets to 3,000 lines, and git is the changelog — `git show` this
// commit to read it back.


// ─────────────────────────────────────────────────────────────
// FOIL TILE — a single card in the vault, buyable in ANY foil tier.
// The tier selector lets a player choose the exact foil they want;
// the art, gem, price, and owned-state all track the selection.
// ─────────────────────────────────────────────────────────────
// ◆ THE VAULT IS PRICED IN DIVINE CRESTS (2026-08-18, the endgame
// ruling). The numbers did not move — FOIL_VAULT_PRICE has been
// denominated in ◆ since it was written; only the debit was pointed at
// ◕ while the plumbing was proven. `vaultOwned` is the one-per-account
// seal, and the tile reads it so the shelf agrees with the write.
function FoilTile({ entry, collection, vaultOwned, crests, busy, onBuy }) {
  const card = getCard(entry.cardId);
  const [tier, setTier] = useState(entry.tier);          // selected foil
  const [hovered, setHovered] = useState(false);
  const info = HOLO_TIER_INFO[tier] || HOLO_TIER_INFO.holo_silver;
  const cost = getFoilVaultCost(entry, tier);
  const canAfford = crests >= cost;
  // THE MYTHICAL LAW: the Vault sells each card-and-tier ONCE per
  // account, because DC is grindable through the Well — the key is
  // rare, not the supply, so the door counts. Keyed the same way
  // progression.js keys it; one shape, two readers.
  const sold = !!vaultOwned?.[`${entry.cardId}~${tier}`];

  // Owned-in-this-tier check (collection tracks holoTiers per card).
  const owned = (() => {
    const rec = collection?.[entry.cardId];
    if (!rec) return false;
    if (Array.isArray(rec.holoTiers)) return rec.holoTiers.includes(tier);
    if (rec.holoTier) return rec.holoTier === tier;
    return (rec.holoCount || 0) > 0; // legacy fallback
  })();

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: `linear-gradient(135deg, ${info.color}0e, transparent 60%), ${hovered ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB}`,
        border: `1px solid ${hovered ? info.color : info.color + "44"}`,
        borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8,
        boxShadow: hovered ? `0 0 18px ${info.color}55, ${SHADOW_RAISED}` : SHADOW_RAISED,
        transition: "all 0.2s",
      }}>
      {/* Card art + foil */}
      <div style={{ position: "relative", width: "100%", aspectRatio: "2/3", borderRadius: 8, overflow: "hidden",
        border: `1px solid ${info.color}66` }}>
        <img src={cardUrl(entry.cardId)} alt={card?.name || entry.cardId}
          style={{ width: "100%", height: "100%", objectFit: "cover", position: "relative", zIndex: 0 }}
          onError={e => { e.target.style.display = "none"; }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,5,0.5), transparent 40%, rgba(0,0,5,0.75))", zIndex: 1 }} />
        {/* Unified HoloFoil — reflects the CURRENTLY SELECTED tier. */}
        <HoloFoil tier={tier} radius={8} corners={true} style={{ zIndex: 2 }} />
        {/* Tier gem badge — tracks selection */}
        <div style={{ position: "absolute", top: 6, right: 6, zIndex: 3 }}>
          <TierGem color={info.color} size={22} />
        </div>
        {/* Curated spotlight star */}
        {entry.curated && (
          <div style={{ position: "absolute", top: 6, left: 6, zIndex: 3,
            background: "rgba(0,0,0,0.6)", border: `1px solid ${GM.inkGold}`, borderRadius: 8,
            padding: "1px 6px", fontSize: 7, fontWeight: 900, color: GM.inkGoldBright, letterSpacing: 1 }}>
            ✦ SIGNATURE
          </div>
        )}
        {/* Owned-in-this-tier ribbon */}
        {owned && (
          <div style={{ position: "absolute", bottom: 26, left: 8, zIndex: 3,
            background: "rgba(0,0,0,0.7)", border: `1px solid ${info.color}`, borderRadius: 8,
            padding: "1px 7px", fontSize: 7, fontWeight: 900, color: info.color, letterSpacing: 1 }}>
            OWNED
          </div>
        )}
        {/* Card name pinned to bottom of art */}
        <div style={{ position: "absolute", left: 8, right: 8, bottom: 6, zIndex: 3,
          fontSize: 11, fontWeight: 900, fontStyle: "italic", color: "#fff", lineHeight: 1.15,
          textShadow: "0 1px 4px rgba(0,0,0,0.95)", fontFamily: GM.font }}>
          {card?.name || entry.cardName || entry.cardId}
        </div>
      </div>

      {/* Tier selector — pick any of the 9 foils */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
        {TIER_ORDER.map(t => {
          const ti = HOLO_TIER_INFO[t];
          if (!ti) return null;
          const active = t === tier;
          return (
            <button key={t} title={ti.label} onClick={() => setTier(t)}
              style={{
                width: 16, height: 16, borderRadius: "50%", cursor: "pointer", padding: 0,
                background: ti.color,
                border: active ? `2px solid ${GM.vellum}` : `1px solid ${ti.color}88`,
                boxShadow: active ? `0 0 8px ${ti.color}` : "none",
                transform: active ? "scale(1.18)" : "scale(1)", transition: "all 0.12s",
              }} />
          );
        })}
      </div>

      {/* Selected tier label */}
      <div style={{ textAlign: "center", fontSize: 9, fontWeight: 900, color: info.color, letterSpacing: 1, fontFamily: GM.font, textShadow: `0 0 8px ${info.color}44` }}>
        {info.glyph} {info.label.toUpperCase()}{entry.marquee ? "  ·  MARQUEE" : ""}
      </div>

      {/* Buy button — price tracks the selected tier */}
      <button onClick={canAfford && !busy && !sold ? () => onBuy(entry, tier) : undefined}
        title={sold ? "The Vault sells this one once per account." : undefined}
        style={{
          width: "100%", padding: "8px", borderRadius: 8,
          cursor: sold ? "default" : (canAfford && !busy ? "pointer" : "not-allowed"),
          background: sold ? "transparent" : (canAfford ? `${info.color}1e` : "transparent"),
          border: `1px solid ${sold ? info.color + "55" : (canAfford ? info.color : GM.pageBorder)}`,
          color: sold ? GM.vellumFaint : (canAfford ? info.color : GM.pageBorder),
          boxShadow: canAfford && !sold ? "inset 0 1px 0 rgba(226,232,244,0.15)" : "none",
          fontSize: 11, fontWeight: 900, fontFamily: GM.font, transition: "all 0.15s",
        }}>
        {sold ? "◆ CLAIMED" : busy ? "..." : <><DivineCrest size={11} /> {cost}</>}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FOIL VAULT — the full card gallery. Every card in the game,
// buyable in any of the 9 foils. Searchable + filterable by type
// so the ~hundreds of cards stay browsable. New cards appear here
// automatically (the vault is generated from the master list).
// ─────────────────────────────────────────────────────────────

// Card-type filter chips. "All" + one per grimoire type.
const VAULT_TYPE_FILTERS = [
  { key: "all",       label: "All",        match: () => true },
  { key: "signature", label: "✦ Signature", match: (e) => e.curated },
  { key: "escendant", label: "Beings",     match: (e) => e.cardType === "Escendant" },
  { key: "tome",      label: "Tomes",      match: (e) => e.cardType === "Tome" },
  { key: "curio",     label: "Curios",     match: (e) => e.cardType === "Curio" },
  { key: "uhlti",     label: "U'hltis",    match: (e) => e.cardType === "U'hlti" },
  { key: "affinity",  label: "Affinities", match: (e) => e.cardType === "Affinity" },
  { key: "prophecy",  label: "Prophecies", match: (e) => e.cardType === "Prophecy" },
];

function FoilVault({ profile, onBuy, busyId, uid, onMessage }) {
  // ⭐ KING'S GRACE — caP'n: "foil is just King's Grace." The Vault has two
  // counters now: cards, and the same foils worn by your companion.
  const [vaultMode, setVaultMode] = useState("cards");
  const crests = profile?.divineCrests || 0;      // ◆ the Vault's currency
  const vaultOwned = profile?.foilVaultOwned || {};
  const collection = profile?.collection || {};
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const allEntries = FOIL_VAULT; // generated; covers every card

  const filtered = useMemo(() => {
    const f = VAULT_TYPE_FILTERS.find(x => x.key === filter) || VAULT_TYPE_FILTERS[0];
    const q = query.trim().toLowerCase();
    return allEntries.filter(e => {
      if (!f.match(e)) return false;
      if (!q) return true;
      const name = (e.cardName || getCard(e.cardId)?.name || e.cardId).toLowerCase();
      return name.includes(q) || e.cardId.toLowerCase().includes(q);
    });
  }, [allEntries, filter, query]);

  // Curated cards float to the front so the flagships are seen first.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (a.curated !== b.curated) return a.curated ? -1 : 1;
      return (a.cardName || a.cardId).localeCompare(b.cardName || b.cardId);
    });
  }, [filtered]);

  return (
    <div>
      {/* Intro plaque — obsidian slab in a forged-gold metal frame */}
      <div style={{
        padding: 2, clipPath: facet(12), background: GOLD_METAL,
        filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.6)) drop-shadow(0 0 14px rgba(232,184,75,0.12))",
        marginBottom: 14,
      }}>
        <div style={{
          clipPath: facet(11),
          background: `linear-gradient(135deg, ${GM.inkGold}0c, transparent 50%), ${OBSIDIAN_SLAB}`,
          padding: "14px 16px",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          {/* The mark every Vault purchase wears forever */}
          <SourceSigil source="vault" size={46} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.font, letterSpacing: 1, marginBottom: 4, textShadow: textGlow(GM.inkGoldBright) }}>
              ◆ The Foil Vault
            </div>
            <div style={{ fontSize: 10.5, color: GM.vellumDim, lineHeight: 1.7, fontStyle: "italic", textShadow: TEXT_SET }}>
              {vaultMode === "cards" ? (<>
              No gambling. Every card in the game, in any holographic foil you want — yours the moment you buy it.
              Tap the gems beneath a card to choose its foil. Every Vault purchase bears the Vault's keyhole mark.
              </>) : GRACE_VAULT_BLURB}
            </div>
          </div>
        </div>
      </div>

      {/* ── THE TWO COUNTERS — cards, or King's Grace for companions ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["cards", "◈ Cards"], ["graces", "♛ King's Grace — Companions"]].map(([k, label]) => {
          const on = vaultMode === k;
          return (
            <button key={k} onClick={() => setVaultMode(k)} style={{
              flex: 1, padding: "9px 12px", fontSize: 11, fontWeight: 900, letterSpacing: 1,
              clipPath: facetTB(8), border: "none", cursor: "pointer", fontFamily: GM.font,
              background: on ? GOLD_METAL : OBSIDIAN_SLAB,
              color: on ? GM.page : GM.vellumDim,
              filter: on ? `drop-shadow(0 0 8px ${GM.inkGoldBright}55)` : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
            }}>{label}</button>
          );
        })}
      </div>

      {vaultMode === "graces" ? (
        <GraceVault profile={profile} uid={uid} onMessage={onMessage} />
      ) : (<>
      {/* Search — a recessed well cut into the glass */}
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search cards…"
        style={{
          width: "100%", boxSizing: "border-box", padding: "10px 14px", marginBottom: 10,
          background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`, borderRadius: 10,
          boxShadow: SHADOW_RECESS,
          color: GM.vellum, fontSize: 12, fontFamily: GM.font, outline: "none",
        }}
      />

      {/* Type filter chips — faceted shards; the active one EMITS */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {VAULT_TYPE_FILTERS.map(f => {
          const active = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{
                padding: "7px 13px", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap",
                clipPath: facetTB(7), border: "none",
                borderTop: active ? "1px solid #f5d878" : "1px solid transparent",
                background: active ? `linear-gradient(135deg, ${GM.inkGold}, ${GM.inkGoldDim})` : OBSIDIAN_SLAB,
                color: active ? GM.page : GM.vellumDim,
                boxShadow: active
                  ? `inset 0 1px 0 rgba(245,216,120,0.6)`
                  : "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 8px rgba(0,0,0,0.4)",
                filter: active ? `drop-shadow(0 0 8px ${GM.inkGoldBright}55)` : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
                cursor: "pointer", fontFamily: GM.font, transition: "all 0.15s",
              }}>
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Count */}
      <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1, marginBottom: 10, fontFamily: GM.font }}>
        {sorted.length} CARD{sorted.length === 1 ? "" : "S"}
      </div>

      {/* Grid */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: GM.vellumFaint, fontStyle: "italic", fontSize: 12 }}>
          No cards match your search.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))", gap: 12 }}>
          {sorted.map(entry => (
            <FoilTile key={entry.id}
              entry={entry}
              collection={collection}
              vaultOwned={vaultOwned}
              crests={crests}
              busy={busyId === entry.id}
              onBuy={onBuy}
            />
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BOUTIQUE CATEGORY MAP — exhaustive, data-driven.
// Every pack ID is assigned a category. Unlisted IDs fall into
// "more" so a pack can NEVER be orphaned/invisible again.
// ─────────────────────────────────────────────────────────────
// ⭐ ONE SIGIL FOR THE CORE (caP'n, 2026-08-22): *"The specific class
// section that sells class only packs like Phantom Mastery should
// share the exact same sigil as the Support and Tomes Packs because
// they are all 'Core' you see? there was no need to make a core
// section so delete that one and let's leave these but unify them with
// one single Sigil."*
//
// So School, Tomes and Support are ONE THING wearing three labels, and
// they say so with a shared glyph and a shared colour. The Foil and
// Activity shelves keep their own, because they are genuinely not
// core — one sells a finish and the other sells a place you have been.
// ⚠ ONE COLOUR AND ONE GLYPH FOR ALL THREE DRAWERS. The rail glyph is a
// typographic ◈ (a tab is text); the SIGIL the packs and their cards
// actually wear is `core` in SourceSigil.js. Both say the same thing at
// two scales, which is the point — see THE CORE banner in progression.js.
const CORE_SIGIL = { glyph: "◈", color: "#d4a84b" };

// ⭐ 2026-08-22 — THE THREE CORE ROWS ARE DERIVED, NOT TYPED.
// They used to be three hand-written rows carrying their own copies of
// the pack-id lists. Those lists now live in CORE_DRAWERS beside the
// PULL that has to agree with them, and this builds the category rows
// off that table. Adding a core pack is one id in ONE file and it
// arrives on the right shelf, wearing the Core sigil, dealing its
// drawer's card count — with no chance of landing in two of those three
// and being silently wrong in the third.
const CORE_CATEGORY_DEFS = Object.entries(CORE_DRAWERS).map(([key, d]) => ({
  key, label: d.label, name: d.name, subtitle: d.subtitle,
  glyph: CORE_SIGIL.glyph, color: CORE_SIGIL.color,
  ids: d.ids,
}));

const CATEGORY_DEFS = [
  // ── THE CORE, in three drawers, behind one door ──────────────
  ...CORE_CATEGORY_DEFS,

  // ══ SHELVED 2026-08-22 — FOIL AND ACTIVITIES CAME DOWN ══════
  // ⭐ caP'n: *"delete Foils and Activities tab and boosters,
  // redundant and strips rarity and meaning from chasing it in the
  // story mode and remember we already have the foil vault to get
  // individual cards — doing packs with a whole new sigil/mark etc is
  // just too much."*
  //
  // Same ruling he made about Premier, applied to the last two places
  // it was still true. A guaranteed-foil PACK and a Foil VAULT are two
  // doors onto the same thing, and the pack is the one that cheapens
  // the finish because it deals foils by the handful. An ACTIVITY pack
  // sells the activity's cards to a player who never went, which is
  // exactly the "chase it in story mode" the activity exists for.
  //
  // ⚠ THE ROWS STAY, `shelved: true`, EXACTLY AS CORE/PREMIER/CHAOS
  // DO. They give no shelf — AISLES does not list them — but they
  // still PLACE their packs, so buildCategories does not sweep eleven
  // packs into the synthesized "More" bucket and grow a shelf for it,
  // putting the deleted sections straight back on the counter under
  // another name. Deleting these two rows RE-CREATES the shelves.
  { key: "holo",      label: "✦ Foil", name: "Holographic Boutique", subtitle: "Shelved", glyph: "✦", color: "#ff66cc", shelved: true,
    // ⚠ `instinct_rising_shadow` REMOVED FROM THIS LIST 2026-08-22.
    // The pack object has been commented out in storyData since the
    // Shadow retirement, so buildCategories silently .filter(Boolean)'d
    // it away and this shelf said eight while showing seven, forever.
    // Not a crash, which is exactly why it survived — a table that
    // names a thing that does not exist is a lie nothing checks.
    // `holo_shadow` stays retired (DOCTRINE §5: never alias it onto
    // another tier); if a genuinely distinct Shadow is ever cut, the id
    // goes back here and into BOUTIQUE_PACKS together.
    ids: ["void_eclipse_mythic", "solar_ascension_gold", "clockwork_dominion_depths", "dragons_wrath_solrend", "supreme_council_aeterni", "void_eclipse_void", "unstable_world_jynxi"] },
  { key: "activity",  label: "⚔ Activities", name: "Activities", subtitle: "Shelved", glyph: "⚔", color: "#c084fc", shelved: true,
    ids: ["activity_minerium1", "activity_gauntlet", "activity_depths", "activity_factions"] },

  // ══ SHELVED 2026-08-22 — PLACERS, NOT SHELVES ═══════════════
  // ⭐ caP'n: *"delete Chaos, Premier, Core, and Limited sections (not
  // the booster packs or the foils — I know that will make the packs
  // inaccessible, that's ok for now, right now I am looking at it and
  // there is just way too much going on)."* And the reasons, verbatim:
  //   CORE    — *"the core is redundant as we already have specific
  //             packs for tomes beings etc."*
  //   PREMIER — *"premier was just too much and we don't want too many
  //             clones because endless depths is 1 type of holo and 1
  //             sigil right, then there's another one from foil vault
  //             ok, but one more from a pack? that's just too much, it
  //             loses rarity and just makes the depths feel slightly
  //             pointless."*
  //
  // ⚠ THESE THREE ROWS STAY AND THAT IS THE WHOLE TRICK. They give no
  // shelf — AISLES below simply does not list them — but they still
  // PLACE their packs, so buildCategories does not sweep sixteen packs
  // into the synthesized "More" bucket, which the orphan net would then
  // grow a shelf for and put the deleted sections straight back on the
  // counter under a different name. Deleting these rows re-creates the
  // shelves. Do not tidy them away.
  { key: "core",    label: "◆ Core", name: "Core Archetypes", subtitle: "Shelved", glyph: "◆", color: "#d4a84b", shelved: true,
    ids: ["void_eclipse", "solar_ascension", "clockwork_dominion", "dragons_wrath", "supreme_council", "instinct_rising", "unstable_world"] },
  { key: "premier", label: "◆ Premier", name: "Premier Experiences", subtitle: "Shelved", glyph: "◆", color: "#ec4899", shelved: true,
    ids: ["all_stars_mythic", "depths_sanctuary", "hybrid_chaos", "affinity_ascension", "prophecy_oracle"] },
  { key: "chaos",   label: "✶ Chaos", name: "The Testing Playground", subtitle: "Shelved", glyph: "✶", color: "#f472b6", shelved: true,
    ids: ["randomizer_supreme"] },
];

// Build categories, guaranteeing every pack is placed exactly once.
function buildCategories(packs) {
  const placed = new Set();
  const cats = CATEGORY_DEFS.map(def => {
    const catPacks = def.ids.map(id => packs.find(p => p.id === id)).filter(Boolean);
    catPacks.forEach(p => placed.add(p.id));
    return { ...def, packs: catPacks };
  });
  // Catch-all for any pack not assigned above.
  const orphans = packs.filter(p => !placed.has(p.id));
  if (orphans.length) {
    cats.push({ key: "more", label: "◆ More", name: "Further Offerings", subtitle: "Additional Packs",
      glyph: "◆", color: "#d4a84b", packs: orphans });
  }
  return cats;
}

// ── <PackCover> — the pack's own best card, already shining ──────
// ⭐ caP'n, 2026-08-22: *"Pick the top rarest/powerful card of the pack
// and use it as the cover art, not the pictures you created. But very
// important — just like in the foil vault, the player absolutely needs
// to see the card's foil before they even hover it. When I click on
// the foil vault they all shine in their proper foil without touching
// anything; the booster packs should work the exact same way."*
//
// Two changes in one sentence, and the second is the load-bearing one.
// The old shelf drew a procedural WRAPPER (PackArt) and only lit the
// aura on hover, so a player scanning the shelf saw eleven grey
// rectangles and had to touch each one to find out what it was for.
// The Foil Vault has never worked that way and it is the better shelf
// for exactly this reason: every tile is already wearing what it
// sells. So this is the Vault's tile, at pack size — card art, scrim,
// and <HoloFoil> mounted unconditionally.
//
// `coverCardId` is authored per pack in storyData; the tier is the
// pack's own signature (getPackSignatureTier), falling back to silver
// so a pack with no signature still shines rather than reading broken.
// If the art 404s the <img> hides itself and the foil keeps running
// over the slab — which is the same graceful failure the Vault has.
//
// ⚠ PackArt is NOT deleted. It still draws the code plate and the
// wrapper in the PREVIEW MODAL, where the object being described
// genuinely is a sealed packet. It is the SHELF that was showing the
// wrong thing.
// ⭐ `tierOverride` added 2026-08-22 — the foil-bubble preview on the
// shelf tile drives it. Unset = the pack's own signature, exactly as
// before, so every other caller is unchanged.
function PackCover({ pack, w = 88, h = 123, lit = false, tierOverride = null }) {
  const tier = tierOverride || getPackSignatureTier(pack) || "holo_silver";
  const info = HOLO_TIER_INFO[tier] || HOLO_TIER_INFO.holo_silver;
  const aura = getPackAuraColor(pack);
  const card = pack.coverCardId ? getCard(pack.coverCardId) : null;

  // No cover authored → fall back to the wrapper rather than a hole.
  if (!pack.coverCardId) {
    return <PackArt accentColor={aura} code={pack.code} w={w} h={h} foil={isFoilPack(pack)} premium />;
  }

  return (
    <div style={{
      position: "relative", width: w, height: h, borderRadius: 8, overflow: "hidden",
      border: `1px solid ${lit ? info.color : info.color + "66"}`,
      boxShadow: lit ? `0 0 16px ${info.color}66` : `0 2px 8px rgba(0,0,0,0.6)`,
      background: "#0a0710", transition: "all .18s",
    }}>
      <img
        src={cardUrl(pack.coverCardId)}
        alt={card?.name || pack.name}
        style={{ width: "100%", height: "100%", objectFit: "cover", position: "relative", zIndex: 0 }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      <div style={{ position: "absolute", inset: 0, zIndex: 1,
        background: "linear-gradient(to bottom, rgba(0,0,5,0.55), transparent 38%, rgba(0,0,5,0.82))" }} />
      {/* ⭐ ALWAYS MOUNTED. Never gated on hover — that is the ask. */}
      <HoloFoil tier={tier} radius={8} corners={true} style={{ zIndex: 2 }} />
      {/* ⚠ THE CORNER GEM IS GONE (caP'n, 2026-08-22): *"remove that grey
          little star in the top right of all cards, we don't need it any
          more — the foil marquees and everything else we have work way
          better now, it was good scaffolding."* It predated <HoloFoil>
          running unconditionally on the tile: back when a cover was a grey
          rectangle until you hovered it, the gem was the only thing saying
          what was inside. The cover now WEARS the foil, the foil bubbles
          name every tier the pack can roll, and the tier chip under the
          name spells the current one out in words. Three better answers to
          the question the gem was asking, so it was a fourth thing in the
          corner saying nothing new. TierGem itself is untouched and still
          used by the bubbles, the chip and the FOILS row. */}
      {/* The pack code, kept from the wrapper — it is the one thing
          the card art cannot say, and collectors read codes. */}
      <div style={{ position: "absolute", left: 6, bottom: 5, zIndex: 3,
        fontFamily: "ui-monospace, Menlo, monospace", fontSize: 7.5, fontWeight: 700,
        letterSpacing: 2, color: "#ffe9a8", textShadow: "0 1px 3px rgba(0,0,0,.95)" }}>
        {pack.code}
      </div>
    </div>
  );
}

// Is this a guaranteed-foil boutique pack?
const isFoilPack = (pack) => !!pack?.holoGuarantee;

// ── packFoilTiers — EXACTLY which foils this pack can produce ──────
// ⭐ caP'n, 2026-08-22: *"remember to show exactly the type of foil a
// booster pack can come with on the front, use the foil vault bubble
// preview design we have where you can click on the marquee and
// preview the foils inside the booster pack."*
//
// This was already computed, correctly, INSIDE <PackContents> — which
// is the detail panel, i.e. after you have already clicked the pack.
// The shelf itself said nothing, so the one fact a foil-chaser is
// actually shopping on was one click behind. Hoisted to a module
// function so the tile and the panel read the SAME list; two copies of
// this is the PACK_COSTS shape and it would drift the first time a
// tier is added.
//
// Guaranteed-foil packs yield exactly their holoTiers. Everything else
// can roll silver or gold on its 15% slot, plus its own signature tier
// if it has one — that is rollHoloTier + the signature, not a guess.
function packFoilTiers(pack) {
  const sig = getPackSignatureTier(pack);
  if (pack?.holoGuarantee && Array.isArray(pack.holoTiers)) return [...new Set(pack.holoTiers)];
  return [...new Set(["holo_silver", "holo_gold", ...(sig ? [sig] : [])])];
}

// ─────────────────────────────────────────────────────────────
// <PackPedestal> — ONE pack on the shelf
// ─────────────────────────────────────────────────────────────
// Was an inline block inside PackShelf's .map(). It needed its own
// useState the moment the foil bubbles arrived (each tile previews
// independently), and a hook cannot live inside a .map callback — so
// it is a component now. Nothing about the look changed in the move;
// the two ADDITIONS are marked ⭐ below.
function PackPedestal({ pack, i, canAfford, isSelected, onSelectPack, onHover }) {
  const foil = isFoilPack(pack);
  // 2.0: the aura around the pack IS the special holo inside it.
  const sigTier = getPackSignatureTier(pack);
  const sigInfo = sigTier ? HOLO_TIER_INFO[sigTier] : null;
  const aura = getPackAuraColor(pack);

  // ⭐ THE FOIL BUBBLES (caP'n, 2026-08-22). The Foil Vault's tier
  // selector, at pack scale: one bubble per foil this pack can
  // actually produce, click to wear it. `preview` is null until the
  // player touches one, so an untouched shelf looks exactly as it did
  // — every tile already shining in its own signature — and the
  // bubbles are an offer, not a state the shelf starts in.
  //
  // ⚠ The bubbles stopPropagation. They sit INSIDE the pedestal's
  // click target, and without it previewing a foil would also buy-
  // select the pack, which is the one thing a player poking at
  // finishes does not want.
  const tiers = packFoilTiers(pack);
  const [preview, setPreview] = useState(null);

  // ⭐ THE PACK'S OWN SIGIL, beside the price. Same mark the pull
  // stamps on every card out of this pack (getPackSourceId reads the
  // pull's own table), so the shelf, the card and the collection all
  // say the same thing about where a card is from.
  const sourceId = getPackSourceId(pack.id);

  return (
    <div
      className="pack-pedestal"
      onClick={canAfford ? () => onSelectPack(pack) : undefined}
      onMouseEnter={() => onHover(pack)}
      onMouseLeave={() => onHover(null)}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        cursor: canAfford ? "pointer" : "not-allowed",
        opacity: canAfford ? 1 : 0.4,
        filter: isSelected ? `drop-shadow(0 0 14px ${aura})` : "none",
        animation: `caseFloat ${3 + (i % 3) * 0.5}s ease-in-out ${i * 0.25}s infinite`,
      }}>
      {/* The wrapped pack + signature-tier aura halo behind it */}
      <div style={{ position: "relative" }}>
        {/* Aura glow — colored by the holo tier inside */}
        <div style={{
          position: "absolute", inset: -8, borderRadius: 16,
          background: `radial-gradient(ellipse at 50% 45%, ${aura}55, ${aura}18 55%, transparent 75%)`,
          filter: "blur(3px)", pointerEvents: "none",
          animation: sigInfo ? `caseFloat ${4 + (i % 2)}s ease-in-out infinite` : "none",
        }} />
        <div style={{ position: "relative" }}>
          <PackCover pack={pack} w={88} h={123} lit={isSelected} tierOverride={preview} />
        </div>
        {/* FOIL ribbon for guaranteed-foil packs */}
        {foil && (
          <div style={{
            position: "absolute", top: 6, left: -4, zIndex: 2,
            background: `linear-gradient(135deg, ${aura}, ${aura}aa)`,
            color: "#0a0005", fontSize: 6.5, fontWeight: 900, letterSpacing: 1,
            padding: "2px 6px", borderRadius: 3, fontFamily: GM.font,
            boxShadow: `0 0 10px ${aura}aa`,
          }}>100% FOIL</div>
        )}
      </div>

      {/* ⭐ FOIL BUBBLES — the Vault's selector, on the front of the pack */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center", marginTop: 6 }}>
        {tiers.map(t => {
          const ti = HOLO_TIER_INFO[t];
          if (!ti) return null;
          const active = preview === t;
          return (
            <button key={t} title={`Preview ${ti.label}`}
              onClick={() => setPreview(active ? null : t)}
              style={{
                width: 13, height: 13, borderRadius: "50%", cursor: "pointer", padding: 0,
                background: ti.color,
                border: active ? `2px solid ${GM.vellum}` : `1px solid ${ti.color}88`,
                boxShadow: active ? `0 0 8px ${ti.color}` : "none",
                transform: active ? "scale(1.22)" : "scale(1)", transition: "all 0.12s",
              }} />
          );
        })}
      </div>

      {/* Name */}
      <div style={{
        fontSize: 8.5, fontWeight: 900, color: canAfford ? GM.vellum : GM.vellumFaint,
        textAlign: "center", lineHeight: 1.25, marginTop: 6, fontFamily: GM.font,
        letterSpacing: 0.3, minHeight: 22, textShadow: TEXT_SET,
      }}>
        {pack.name.toUpperCase()}
      </div>

      {/* Which foil is being worn right now — names the bubble you pressed,
          and otherwise names the pack's signature, which is what the
          aura has always been signalling without saying. */}
      {(preview ? HOLO_TIER_INFO[preview] : sigInfo) && (
        <div style={{
          display: "flex", alignItems: "center", gap: 4, marginTop: 3,
          background: `${(preview ? HOLO_TIER_INFO[preview] : sigInfo).color}14`,
          border: `1px solid ${(preview ? HOLO_TIER_INFO[preview] : sigInfo).color}55`,
          borderRadius: 8, padding: "1px 7px",
        }}>
          <TierGem color={(preview ? HOLO_TIER_INFO[preview] : sigInfo).color} size={11} glow={false} />
          <span style={{ fontSize: 7.5, fontWeight: 900, color: (preview ? HOLO_TIER_INFO[preview] : sigInfo).color, letterSpacing: 0.5, fontFamily: GM.font }}>
            {(preview ? HOLO_TIER_INFO[preview] : sigInfo).label.toUpperCase()}
          </span>
        </div>
      )}

      {/* Price chip — ⭐ the pack's Sigil now rides beside the coin */}
      <div style={{
        display: "flex", alignItems: "center", gap: 5, marginTop: 4,
        background: canAfford ? `${aura}1a` : "rgba(255,68,68,0.12)",
        border: `1px solid ${canAfford ? aura + "66" : "#ff444455"}`,
        borderRadius: 10, padding: "2px 8px",
      }}>
        <ChocolateCoin size={9} glow={false} style={{ opacity: canAfford ? 1 : 0.45 }} />
        <span style={{ fontSize: 10, fontWeight: 900, color: canAfford ? aura : "#ff6666", fontFamily: GM.font }}>
          {pack.cost}
        </span>
        <span style={{ width: 1, height: 10, background: `${aura}44` }} />
        <SourceSigil source={sourceId} size={14} intensity={canAfford ? 1 : 0.4} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PACK SHELF — boutique display case with procedural pack art.
// Re-forged: the wooden cabinet became an obsidian vitrine —
// black glass panel, amethyst stardust drifting inside the case,
// category-colored caselight, faceted category tabs.
// ─────────────────────────────────────────────────────────────
// ⚠ 4xS PASS, 2026-08-22 — THIS COMPONENT NO LONGER OWNS A TAB ROW.
// It used to render its own category tabs AND its own selection state,
// sitting under the shop's six top-level tabs: twelve flat nav targets
// on one counter, two rails that looked different for no reason, and
// two places to add a shelf. The rail moved up to <ShelfRail> and the
// selection moved up to PackShop, so this is now what its name always
// claimed — a shelf. Give it a category, it displays that category.
// SIMPLE deletes machinery; this deleted a rail and a useState.
function PackShelf({ category, coins, onSelectPack, selectedId, onHover }) {
  const currentCategory = category;
  const currentPacks = currentCategory?.packs || [];

  return (
    <div style={{ position: "relative", width: "100%", margin: "0 auto" }}>

      {/* ⚠ THE HEADER LIVES IN <ShelfHead> NOW. PackShelf used to draw
          its own near-verbatim copy, which meant two headers to keep in
          step and the SMART claim on the aisle table was only
          three-quarters true. The caller draws it above this component
          for every shelf, packs included. */}

      {/* Obsidian vitrine — the display case */}
      <div style={{
        background: `radial-gradient(ellipse at 50% 0%, ${currentCategory?.color}0d 0%, transparent 55%), ${OBSIDIAN_PANEL}`,
        border: `1px solid ${currentCategory?.color}44`,
        borderRadius: 14, padding: "26px 16px 16px", position: "relative",
        boxShadow: `inset 0 0 50px rgba(0,0,0,0.7), 0 8px 32px rgba(0,0,0,0.85), 0 0 30px ${currentCategory?.color}11`,
        overflow: "hidden",
      }}>
        {/* Amethyst stardust drifting inside the glass */}
        <StarDust count={18} />
        {/* Crystalline sheen in the case glass */}
        <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(135deg, transparent 0px, transparent 24px, rgba(192,132,252,0.03) 24px, rgba(192,132,252,0.03) 25px)", pointerEvents: "none" }} />
        {/* Overhead case light */}
        <div style={{
          position: "absolute", top: 0, left: "8%", right: "8%", height: 3,
          background: `linear-gradient(90deg, transparent, ${currentCategory?.color}88, #ffffffcc, ${currentCategory?.color}88, transparent)`,
          borderRadius: "0 0 4px 4px", boxShadow: `0 0 22px ${currentCategory?.color}66`,
        }} />
        {/* Soft floor glow */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 60,
          background: `radial-gradient(ellipse at 50% 100%, ${currentCategory?.color}18, transparent 70%)`,
          pointerEvents: "none",
        }} />

        {/* Pack pedestals */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
          gap: 16, maxHeight: 320, overflowY: "auto", paddingBottom: 6,
          position: "relative",
        }}>
          {currentPacks.map((pack, i) => (
            <PackPedestal
              key={pack.id}
              pack={pack} i={i}
              canAfford={coins >= pack.cost}
              isSelected={selectedId === pack.id}
              onSelectPack={onSelectPack}
              onHover={onHover}
            />
          ))}
        </div>

        {/* Glass shelf reflection line */}
        <div style={{
          marginTop: 10, height: 2,
          background: `linear-gradient(90deg, transparent, ${currentCategory?.color}66, transparent)`,
          borderRadius: 2,
        }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PACK INFO PANEL — shows selected pack details below shelf
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// PACK CONTENTS — the exact cards & holo types a pack can yield.
// Reads the real resolved pool (getPackCardPool) so the preview is
// always truthful, never a guess. Marquee/rare cards listed first.
// ─────────────────────────────────────────────────────────────
function PackContents({ pack }) {
  const pool = useMemo(() => getPackCardPool(pack.id), [pack.id]);
  const auraColor = getPackAuraColor(pack);
  const sigTier = getPackSignatureTier(pack);

  // ⚠ ONE SOURCE. This used to be computed here and nowhere else,
  // which is why the shelf tile could not show it. See packFoilTiers.
  const holoTypes = packFoilTiers(pack);

  // ⭐ 2026-08-22 — THE REAL CARD COUNT, from the table the PULL reads.
  // "10 cards per pack" was typed into two components and true of
  // neither a Tome pack (3) nor a Support pack (4). getPackContents is
  // the same call openPack makes, so the shelf cannot advertise a
  // number the pull does not deal.
  const contents = getPackContents(pack.id, { boutique: !!pack.holoGuarantee });
  const perPack = contents.total;

  // Resolve card names; rares (chase) first, then commons.
  const rareCards   = (pool.rare   || []).map(id => ({ id, card: getCard(id) })).filter(x => x.card);
  const commonCards = (pool.common || []).map(id => ({ id, card: getCard(id) })).filter(x => x.card);
  const totalUnique = rareCards.length + commonCards.length;

  // ── THE PLAQUE — four numbers, four cells, one grid ─────────────
  // ⭐ caP'n, 2026-08-22: *"in the description of the booster packs — I
  // love the numbers and all the information, I just think it needs
  // some 4xS love, it's a bit messy and unorganised, definitely lacking
  // a couple 4's in its design."*
  //
  // WHAT WAS MESSY, precisely. The same four facts were being told in
  // four different visual languages, in three different places, in an
  // order nobody chose: a plain grey sentence outside the box said the
  // pack size and the foil chance; a right-aligned italic whisper in
  // the box header said the pool size; and the chase count was never
  // stated at all — you counted the chips. Four numbers, four
  // treatments, and the two most important ones were the quietest.
  //
  // 4xS, in order:
  //   SIMPLE  — one block instead of three. Everything numeric is above
  //             the divider; everything you can NAME is below it. That
  //             is the whole organising idea and it is why this reads
  //             fast: numbers, then names, never interleaved.
  //   SMART   — every cell is derived. perPack from getPackContents,
  //             chase and pool from the resolved pool, the foil cell
  //             from holoGuarantee. Not one of the four can go stale,
  //             which is what the hardcoded 10 could not say.
  //   SPECIAL — the CHASE cell wears the pack's own aura and the FOIL
  //             cell goes gold-on-dark for a guaranteed pack. The one
  //             number a collector shops on is the one that is lit.
  //   SEXY    — four equal cells, a hairline between them, monospace
  //             figures so the digits line up in a column. It is a
  //             plaque on a display case, which is what a boutique
  //             puts under the thing it is selling.
  //
  // ⚠ FOUR CELLS. If a fifth fact ever earns a place here it does NOT
  // get squeezed in as a fifth column — the grid breaks to 2x2 and
  // stops being a plaque. Take something out or put it below the rule.
  const foilCell = pack.holoGuarantee
    ? { v: "100%", k: "EVERY CARD FOIL", lit: true }
    : { v: "15%",  k: "FOIL PER CARD",   lit: false };

  const Stat = ({ v, k, color, lit }) => (
    <div style={{
      flex: "1 1 0", minWidth: 62, textAlign: "center", padding: "7px 4px",
      background: lit ? `${color}12` : "transparent",
      borderRadius: 7,
    }}>
      <div style={{
        fontFamily: "ui-monospace, Menlo, monospace", fontSize: 17, fontWeight: 900,
        lineHeight: 1, color: color || GM.vellum,
        textShadow: lit ? `0 0 12px ${color}66, 0 1px 2px #000` : TEXT_SET,
      }}>{v}</div>
      <div style={{
        fontSize: 6.5, letterSpacing: 1.4, fontWeight: 900, marginTop: 5,
        color: GM.vellumFaint, fontFamily: GM.font,
      }}>{k}</div>
    </div>
  );

  const Chip = ({ id, card, rare }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: rare ? `${auraColor}12` : "rgba(255,255,255,0.03)",
      border: `1px solid ${rare ? auraColor + "55" : GM.pageBorder}`,
      borderRadius: 8, padding: "2px 8px", fontSize: 9, fontFamily: GM.font,
      color: rare ? GM.vellum : GM.vellumDim, lineHeight: 1.4,
    }}>
      {rare && <span style={{ color: auraColor, fontSize: 8 }}>✦</span>}
      {card.name}
    </span>
  );

  // A titled row below the rule. Every list down here wears the same
  // head — the FOILS row used to be a bare inline label while CHASE and
  // ALSO INSIDE had titles, which is half the reason the block read as
  // three pieces of furniture instead of one.
  const Group = ({ title, color, children, gap = 5 }) => (
    <div style={{ marginBottom: 9 }}>
      <div style={{
        fontSize: 7.5, letterSpacing: 1.6, fontWeight: 900, marginBottom: 6,
        color: color || GM.vellumFaint, fontFamily: GM.font,
      }}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap, alignItems: "center" }}>{children}</div>
    </div>
  );

  return (
    <div style={{
      marginBottom: 14,
      background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`,
      borderRadius: 10, boxShadow: SHADOW_RECESS, overflow: "hidden",
    }}>
      {/* ── THE PLAQUE ───────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "stretch", gap: 2, padding: "8px 8px 6px",
        background: `linear-gradient(180deg, ${auraColor}0e, transparent)`,
      }}>
        <Stat v={perPack} k={perPack === 1 ? "CARD IN PACK" : "CARDS PER PACK"} color={GM.vellum} />
        <div style={{ width: 1, background: GM.pageBorder, margin: "6px 0" }} />
        <Stat v={rareCards.length} k="CHASE CARDS" color={auraColor} lit />
        <div style={{ width: 1, background: GM.pageBorder, margin: "6px 0" }} />
        <Stat v={foilCell.v} k={foilCell.k} color={foilCell.lit ? GM.inkGoldBright : GM.vellum} lit={foilCell.lit} />
        <div style={{ width: 1, background: GM.pageBorder, margin: "6px 0" }} />
        <Stat v={totalUnique} k="IN THE POOL" color={GM.vellum} />
      </div>

      {/* THE RULE. Numbers above it, names below it. */}
      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${auraColor}44, transparent)` }} />

      <div style={{ padding: "11px 14px 4px" }}>
        {/* Which finishes this pack can actually roll */}
        <Group title="◈ FINISHES THIS PACK CAN ROLL" gap={6}>
          {holoTypes.map(t => {
            const info = HOLO_TIER_INFO[t];
            if (!info) return null;
            const isSig = t === sigTier && !pack.holoGuarantee;
            return (
              <span key={t} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: `${info.color}14`, border: `1px solid ${info.color}${isSig ? "" : "55"}`,
                borderRadius: 9, padding: "1px 8px 1px 5px",
                boxShadow: isSig ? `0 0 8px ${info.color}66` : "none",
              }}>
                <TierGem color={info.color} size={13} glow={false} />
                <span style={{ fontSize: 8.5, fontWeight: 900, color: info.color, fontFamily: GM.font }}>
                  {info.label}{isSig ? " ★" : ""}
                </span>
              </span>
            );
          })}
        </Group>

        {/* Chase cards */}
        {rareCards.length > 0 && (
          <Group title="✦ CHASE / RARE" color={auraColor}>
            {rareCards.map(({ id, card }) => <Chip key={id} id={id} card={card} rare />)}
          </Group>
        )}

        {/* Common cards */}
        {commonCards.length > 0 && (
          <div style={{ marginBottom: 9 }}>
            <div style={{
              fontSize: 7.5, letterSpacing: 1.6, fontWeight: 900, marginBottom: 6,
              color: GM.vellumFaint, fontFamily: GM.font,
            }}>◈ ALSO INSIDE</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 92, overflowY: "auto" }}>
              {commonCards.map(({ id, card }) => <Chip key={id} id={id} card={card} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PackInfoPanel({ pack, canAfford, onBuy }) {
  if (!pack) return (
    <div style={{
      textAlign: "center", padding: "20px",
      color: GM.vellumFaint, fontSize: 12, fontStyle: "italic",
      letterSpacing: 1,
    }}>
      Hover over a pack to inspect it
    </div>
  );

  const aura = getPackAuraColor(pack);
  return (
    <div style={{
      background: `linear-gradient(135deg, ${aura}14, transparent 55%), ${OBSIDIAN_SLAB}`,
      border: `1px solid ${aura}44`,
      borderRadius: 14, padding: "18px 20px",
      boxShadow: SHADOW_RAISED,
      animation: "slideUp 0.2s ease-out both",
    }}>
      {/* ⭐ 2026-08-22 — THE BADGE IS THE PACK'S SIGIL NOW, not `pack.icon`.
          caP'n: *"there's a star there for Space Ascendants, let's remove
          that (for every card pack remove whatever the symbol is there)
          and put the card's actual Sigil there so we can see it there as
          well."* `pack.icon` was a decorative unicode glyph authored per
          pack in storyData — ★, ◇, ⟳, ◆ — and it meant nothing: two packs
          could share one and none of them matched the mark their cards
          actually carry. This is that mark, from the table the PULL
          writes, so the badge, the shelf chip and every card out of the
          pack now agree. `pack.icon` is left in storyData rather than
          swept — the wrapper art in the preview modal still reads it. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10,
          background: pack.gradient,
          border: `1px solid ${aura}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          boxShadow: `0 0 16px ${aura}44`,
        }}>
          <SourceSigil source={getPackSourceId(pack.id)} size={34} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: aura, letterSpacing: 3, marginBottom: 2 }}>
            {pack.subtitle?.toUpperCase()}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum, fontFamily: GM.font, textShadow: TEXT_SET }}>
            {pack.name}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: GM.vellumDim, lineHeight: 1.8, marginBottom: 14, fontStyle: "italic" }}>
        {pack.description}
      </div>

      {/* ⭐ 2026-08-22 — THE LOOSE PULL-RATE ROW IS GONE. It printed a
          HARDCODED "10 cards per pack" (here and again in the preview
          modal — two claims, one number, and both wrong the moment a
          Tome pack became three cards) and it sat OUTSIDE the panel
          that lists what is actually inside. Its two facts are now two
          cells of THE PLAQUE inside <PackContents>, read from
          getPackContents. One block, one border, four numbers. */}
      <PackContents pack={pack} />

      <div style={{ fontSize: 11, color: GM.inkGold, fontStyle: "italic", marginBottom: 14, textAlign: "center", textShadow: TEXT_SET }}>
        "{pack.flavor}"
      </div>

      <button
        onClick={canAfford ? onBuy : undefined}
        style={{
          width: "100%", padding: "14px",
          background: canAfford ? OBSIDIAN_SLAB_LIT : "transparent",
          border: `2px solid ${canAfford ? aura : GM.pageBorder}`,
          borderRadius: 10, cursor: canAfford ? "pointer" : "not-allowed",
          color: canAfford ? aura : GM.pageBorder,
          fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
          boxShadow: canAfford ? `0 0 20px ${aura}33, inset 0 1px 0 rgba(226,232,244,0.18)` : "none",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        <span>◆</span>
        <span>{pack.cost} Chocolate Coins</span>
        {!canAfford && <span style={{ fontSize: 10 }}>— Not enough</span>}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PACK SHOP — the full shop screen with shelf + info panel
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// PACK PREVIEW MODAL — the new "norm": clicking any pack opens
// THIS first. Two steps:
//   step "preview"  → full pack details + "Purchase Pack" button
//   step "confirm"  → "Purchase this pack?" with Confirm / Cancel
// Confirming calls onConfirm(pack) which runs the real openPack flow.
// Closing (X / backdrop / Keep Browsing) returns to the shelf.
// ─────────────────────────────────────────────────────────────
function PackPreviewModal({ pack, canAfford, onConfirm, onClose }) {
  const [step, setStep] = useState("preview");
  // Always reset to preview whenever a new pack is opened.
  const lastPackId = useRef(pack?.id);
  if (pack && pack.id !== lastPackId.current) {
    lastPackId.current = pack.id;
    if (step !== "preview") setStep("preview");
  }
  useModalKeys(onClose);
  if (!pack) return null;

  const aura = getPackAuraColor(pack);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 600,
        background: "radial-gradient(ellipse at 50% 40%, rgba(23,15,48,0.85) 0%, rgba(4,2,10,0.92) 100%)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "slideUp 0.18s ease-out both",
      }}
    >
      {/* Shell holds the house frame; details scroll in the inner region
          (TerrorFrame law: frame on a non-scrolling shell — SoulModal pattern) */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative", width: "100%", maxWidth: 440,
          maxHeight: "88vh",
          background: `linear-gradient(160deg, ${aura}0a, transparent 45%), ${OBSIDIAN_PANEL}`,
          border: `1px solid ${aura}55`, borderRadius: 16,
          boxShadow: `0 0 40px ${aura}33, 0 12px 50px rgba(0,0,0,0.7), inset 0 1px 0 rgba(245,216,120,0.2)`,
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <TerrorFrame />
        {/* Close (X) — pinned to the shell so it never scrolls away */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 8,
            width: 30, height: 30, borderRadius: "50%",
            background: OBSIDIAN_SLAB, border: `1px solid ${GM.inkBrown}`,
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.18), 0 2px 6px rgba(0,0,0,0.6)",
            color: GM.silver, fontSize: 15, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: GM.font, lineHeight: 1,
          }}
        >✕</button>

        <div style={{ overflowY: "auto", padding: 18, minHeight: 0 }}>

        {/* ── PURCHASE BAR (top) ── */}
        {step === "preview" ? (
          <button
            onClick={canAfford ? () => setStep("confirm") : undefined}
            style={{
              width: "100%", padding: "13px", marginBottom: 16,
              background: canAfford ? `linear-gradient(135deg, ${aura}dd, ${aura}99)` : "transparent",
              border: `2px solid ${canAfford ? aura : GM.pageBorder}`,
              borderRadius: 10, cursor: canAfford ? "pointer" : "not-allowed",
              color: canAfford ? "#0a0005" : GM.pageBorder,
              fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
              letterSpacing: 0.5,
              boxShadow: canAfford ? `0 0 20px ${aura}44, inset 0 1px 0 rgba(255,255,255,0.35)` : "none",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <span>◆</span>
            <span>Purchase Pack — {pack.cost}</span>
            {!canAfford && <span style={{ fontSize: 10 }}>(Not enough)</span>}
          </button>
        ) : (
          <div style={{
            marginBottom: 16, padding: "14px 16px",
            background: `linear-gradient(135deg, ${aura}18, transparent 55%), ${OBSIDIAN_SLAB}`,
            border: `1px solid ${aura}55`, borderRadius: 10, textAlign: "center",
            boxShadow: SHADOW_RAISED,
          }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, marginBottom: 4, textShadow: TEXT_SET }}>
              Purchase this pack?
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, fontFamily: GM.font, marginBottom: 12 }}>
              {pack.name} — {pack.cost} Chocolate Coins
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => onConfirm(pack)}
                style={{
                  flex: 1, padding: "11px", background: `linear-gradient(135deg, ${aura}dd, ${aura}99)`,
                  border: `2px solid ${aura}`, borderRadius: 9, cursor: "pointer",
                  color: "#0a0005", fontFamily: GM.font, fontSize: 13, fontWeight: 900, fontStyle: "italic",
                  boxShadow: `0 0 18px ${aura}44, inset 0 1px 0 rgba(255,255,255,0.35)`,
                }}
              >◆ Confirm</button>
              <button
                onClick={() => setStep("preview")}
                style={{
                  flex: 1, padding: "11px", background: "transparent",
                  border: `1px solid ${GM.pageBorder}`, borderRadius: 9, cursor: "pointer",
                  color: GM.vellumDim, fontFamily: GM.font, fontSize: 13,
                }}
              >Cancel</button>
            </div>
          </div>
        )}

        {/* ── PACK DETAILS (reused panel body) ── */}
        <PackPreviewBody pack={pack} />

        {/* Keep browsing */}
        <button
          onClick={onClose}
          style={{
            width: "100%", marginTop: 14, padding: "10px", background: "transparent",
            border: `1px solid ${GM.pageBorder}`, borderRadius: 9, cursor: "pointer",
            color: GM.vellumFaint, fontFamily: GM.font, fontSize: 12,
          }}
        >← Keep Browsing</button>

        </div>
      </div>
    </div>
  );
}

// Pack detail body — icon, name, description, pull rates, contents, flavor.
// (Same content the old hover panel showed, minus its buy button — the
// modal owns the purchase bar now.)
function PackPreviewBody({ pack }) {
  const aura = getPackAuraColor(pack);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10,
          background: pack.gradient,
          border: `1px solid ${aura}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, boxShadow: `0 0 16px ${aura}44`,
        }}>
          {/* ⚠ THE LAST `pack.icon` ON A PACK HEADER. PackInfoPanel was
              switched to the Sigil on 2026-08-22 and this modal — the
              same header, one click deeper — was missed, so the shelf
              said Core and the thing you were about to buy said ◲.
              Two headers, one mark, from the table the PULL writes. */}
          <SourceSigil source={getPackSourceId(pack.id)} size={34} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: aura, letterSpacing: 3, marginBottom: 2 }}>
            {pack.subtitle?.toUpperCase()}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum, fontFamily: GM.font, textShadow: TEXT_SET }}>
            {pack.name}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: GM.vellumDim, lineHeight: 1.8, marginBottom: 14, fontStyle: "italic" }}>
        {pack.description}
      </div>

      {/* Same removal as the info panel — the numbers live in the
          plaque inside <PackContents> now, once, for both callers. */}
      <PackContents pack={pack} />

      {pack.flavor && (
        <div style={{ fontSize: 11, color: GM.inkGold, fontStyle: "italic", marginTop: 14, textAlign: "center", textShadow: TEXT_SET }}>
          "{pack.flavor}"
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SIGNATURE SHELF — the Emporium's cosmetics counter.
// Backdrops today; any future "cool stuff" (auras, flourishes,
// music, room props) gets shelved here too. Same catalog and
// same progression fns as the Backdrop Atelier — ONE source of
// truth, two storefronts.
//   • Purchases: unlockBackdrop → spendCoins transaction (delta,
//     atomic) + append-only unlock. GRANT-BEFORE-FLAG honored.
//   • Live listeners on chocolateCoins + roomBackdrops, so the
//     shelf updates instantly after buy/equip (the profile prop
//     is a snapshot; the shelf doesn't trust it).
// ─────────────────────────────────────────────────────────────
function SignatureShelf({ uid }) {
  const [coins, setCoins]     = useState(0);
  const [unlocked, setUnlocked] = useState({});
  const [equipped, setEquipped] = useState("obsidian");
  const [busy, setBusy]         = useState(null);
  const [note, setNote]         = useState(null); // { text, color }

  useEffect(() => {
    if (!uid) return;
    const offA = onValue(ref(db, `profiles/${uid}/chocolateCoins`),
      (s) => setCoins(s.val() || 0));
    const offB = onValue(ref(db, `profiles/${uid}/roomBackdrops`),
      (s) => {
        const v = s.val() || {};
        setUnlocked(v.unlocked || {});
        setEquipped(typeof v.equipped === "string" && v.equipped ? v.equipped : "obsidian");
      });
    return () => { offA(); offB(); };
  }, [uid]);

  const isOwned = (bd) => bd.cost === 0 || !!unlocked[bd.id];

  // D10 — hovering a signature piece dresses the stage around the
  // Emporium with it: try the void on before you buy it. Cleared on
  // unmount so leaving the counter never leaves a fitting on.
  useEffect(() => () => previewBackdrop(null), []);

  const handleBuy = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id); setNote(null);
    const res = await unlockBackdrop(uid, bd.id, bd.cost, "emporium_signature");
    if (res.ok) {
      setNote({ text: `◆ ${bd.name} unlocked. Equip it and walk the rooms.`, color: BACKDROP_RARITY[bd.rarity].color });
    } else if (res.reason === "insufficient-coins") {
      setNote({ text: `Not enough Chocolate Coins for ${bd.name}.`, color: "#f43f5e" });
    } else {
      setNote({ text: "The counter jammed — nothing was charged. Try again.", color: "#f43f5e" });
    }
    setBusy(null);
  };

  const handleEquip = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id);
    await equipBackdrop(uid, bd.id);
    setBusy(null);
  };

  return (
    <div>
      <div style={{
        fontSize: 11, color: GM.vellumFaint, marginBottom: 14,
        lineHeight: 1.7, fontStyle: "italic",
      }}>
        Signature pieces dress the void around every room — equip anywhere,
        appear everywhere. One equipped backdrop follows you through the
        entire Emperium.
      </div>

      {note && (
        <div style={{
          padding: "8px 14px", marginBottom: 12, fontSize: 11.5,
          fontStyle: "italic", color: note.color, textAlign: "center",
          letterSpacing: 1,
        }}>{note.text}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 30 }}>
        {BACKDROPS.map(bd => {
          const rar = BACKDROP_RARITY[bd.rarity];
          const owned = isOwned(bd);
          const isEq = equipped === bd.id;
          const canAfford = coins >= bd.cost;
          return (
            <div key={bd.id}
              onMouseEnter={CAN_HOVER ? () => previewBackdrop(bd.id) : undefined}
              onMouseLeave={CAN_HOVER ? () => previewBackdrop(null) : undefined}
              style={{
              border: `1px solid ${isEq ? rar.color : GM.pageBorder}`,
              borderRadius: 12, overflow: "hidden",
              background: OBSIDIAN_SLAB,
              boxShadow: isEq ? `0 0 16px ${rar.color}33, ${SHADOW_RAISED}` : SHADOW_RAISED,
            }}>
              {/* Live preview — the backdrop rendering itself */}
              <div style={{ position: "relative", height: 96, overflow: "hidden" }}>
                {bd.render()}
                <div style={{
                  position: "absolute", top: 8, left: 8,
                  fontSize: 8, fontWeight: 900, letterSpacing: 2,
                  color: rar.color, textShadow: "0 1px 3px #000",
                }}>{rar.label}</div>
                {isEq && (
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: rar.color, textShadow: "0 1px 3px #000",
                  }}>◆ EQUIPPED</div>
                )}
              </div>

              <div style={{ padding: "10px 12px 12px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.vellum, fontFamily: GM.font, textShadow: TEXT_SET }}>{bd.name}</div>
                  <div style={{ fontSize: 10, fontStyle: "italic", color: GM.vellumFaint, marginTop: 2, lineHeight: 1.45 }}>{bd.lore}</div>
                </div>
                {!owned ? (
                  <button
                    onClick={canAfford && busy !== bd.id ? () => handleBuy(bd) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: canAfford ? "pointer" : "not-allowed",
                      background: canAfford ? OBSIDIAN_SLAB_LIT : "transparent",
                      border: `1px solid ${canAfford ? rar.color : GM.pageBorder}`,
                      color: canAfford ? rar.color : GM.pageBorder,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap", opacity: canAfford ? 1 : 0.4,
                    }}>
                    {busy === bd.id ? "..." : <><ChocolateCoin size={10} /> {bd.cost.toLocaleString()}</>}
                  </button>
                ) : (
                  <button
                    onClick={!isEq && busy !== bd.id ? () => handleEquip(bd) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: isEq ? "default" : "pointer",
                      background: isEq ? "rgba(232,184,75,0.06)" : "transparent",
                      border: `1px solid ${isEq ? GM.pageBorder : GM.inkGold + "66"}`,
                      color: isEq ? GM.vellumDim : GM.inkGoldBright,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}>
                    {isEq ? "WORN" : "EQUIP"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DRAW PULSE SHELF — the cue counter, beside the backdrops.
// SignatureShelf's own docstring called this shot: "backdrops
// today; any future cool stuff (auras, flourishes, music, room
// props) gets shelved here too." This is that.
//
// Every tile PREVIEWS LIVE — two real draw tiles breathing the
// exact colours they will breathe on the board. Nobody buys a
// swatch here; they buy the thing they can already see.
//
// Economy is byte-identical to the backdrop counter above:
// unlockDrawPulse → atomic spendCoins + append-only unlock,
// GRANT-BEFORE-FLAG, live listeners on chocolateCoins + drawPulse
// so the shelf updates the instant a purchase lands.
// ─────────────────────────────────────────────────────────────
function DrawPulseShelf({ uid }) {
  const [coins, setCoins]     = useState(0);
  const [unlocked, setUnlocked] = useState({});
  const [equipped, setEquipped] = useState(DEFAULT_PULSE_ID);
  const [busy, setBusy]         = useState(null);
  const [note, setNote]         = useState(null);

  useEffect(() => {
    if (!uid) return;
    const offA = onValue(ref(db, `profiles/${uid}/chocolateCoins`),
      (s) => setCoins(s.val() || 0));
    const offB = onValue(ref(db, `profiles/${uid}/drawPulse`),
      (s) => {
        const v = s.val() || {};
        setUnlocked(v.unlocked || {});
        setEquipped(typeof v.equipped === "string" && v.equipped ? v.equipped : DEFAULT_PULSE_ID);
      });
    return () => { offA(); offB(); };
  }, [uid]);

  // cost 0 is owned by definition — Lyt is issued, not sold.
  const isOwned = (p) => p.cost === 0 || !!unlocked[p.id];

  const handleBuy = async (p) => {
    if (!uid || busy) return;
    setBusy(p.id); setNote(null);
    const res = await unlockDrawPulse(uid, p.id, p.cost, "emporium_signature");
    if (res.ok) {
      setNote({ text: `\u25c6 ${p.name} unlocked. Equip it and it lights your first turn.`,
                color: PULSE_RARITY[p.rarity]?.color || GM.inkGold });
    } else if (res.reason === "insufficient-coins") {
      setNote({ text: `Not enough Chocolate Coins for ${p.name}.`, color: "#f43f5e" });
    } else {
      setNote({ text: "The counter jammed \u2014 nothing was charged. Try again.", color: "#f43f5e" });
    }
    setBusy(null);
  };

  const handleEquip = async (p) => {
    if (!uid || busy) return;
    setBusy(p.id);
    await equipDrawPulse(uid, p.id);
    setBusy(null);
  };

  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ height: 1, background: GM.pageBorder, marginBottom: 18 }} />
      <div style={{
        fontSize: 10, letterSpacing: 4, color: GM.inkGoldBright,
        fontWeight: 900, fontFamily: GM.font, marginBottom: 6,
        textShadow: textGlow(GM.inkGoldBright),
      }}>✦ DRAW PULSES</div>
      <div style={{
        fontSize: 11, color: GM.vellumFaint, marginBottom: 14,
        lineHeight: 1.7, fontStyle: "italic",
      }}>
        On your first turn the piles you may still draw from breathe, so no
        Aenarchist ever walks past their draw. Gold for the Luxmoire, red for
        the Grimoire — unless you'd rather it were something else.
      </div>

      {note && (
        <div style={{
          padding: "8px 14px", marginBottom: 12, fontSize: 11.5,
          fontStyle: "italic", color: note.color, textAlign: "center", letterSpacing: 1,
        }}>{note.text}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 30 }}>
        {DRAW_PULSES.map(p => {
          const rar = PULSE_RARITY[p.rarity] || PULSE_RARITY.fable;
          const owned = isOwned(p);
          const isEq = equipped === p.id;
          const canAfford = coins >= p.cost;
          return (
            <div key={p.id} style={{
              border: `1px solid ${isEq ? p.lux.base : GM.pageBorder}`,
              borderRadius: 12, overflow: "hidden",
              background: OBSIDIAN_SLAB,
              boxShadow: isEq ? `0 0 16px ${p.lux.base}33, ${SHADOW_RAISED}` : SHADOW_RAISED,
            }}>
              {/* LIVE PREVIEW — the real tiles, really pulsing */}
              <div style={{
                position: "relative", padding: "16px 12px 14px",
                background: `radial-gradient(ellipse at 50% 55%, ${p.lux.base}14, transparent 70%)`,
              }}>
                <DrawPulsePreview pulse={p} />
                <div style={{
                  position: "absolute", top: 8, left: 10,
                  fontSize: 8, fontWeight: 900, letterSpacing: 2,
                  color: rar.color, textShadow: "0 1px 3px #000",
                }}>{rar.label}</div>
                {isEq && (
                  <div style={{
                    position: "absolute", top: 8, right: 10,
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: p.lux.base, textShadow: "0 1px 3px #000",
                  }}>◆ EQUIPPED</div>
                )}
              </div>

              <div style={{ padding: "10px 12px 12px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 900, fontStyle: "italic",
                    color: GM.vellum, fontFamily: GM.font, textShadow: TEXT_SET,
                  }}>{p.name}</div>
                  <div style={{
                    fontSize: 10, fontStyle: "italic", color: GM.vellumFaint,
                    marginTop: 2, lineHeight: 1.45,
                  }}>{p.lore}</div>
                </div>
                {!owned ? (
                  <button
                    onClick={canAfford && busy !== p.id ? () => handleBuy(p) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: canAfford ? "pointer" : "not-allowed",
                      background: canAfford ? OBSIDIAN_SLAB_LIT : "transparent",
                      border: `1px solid ${canAfford ? rar.color : GM.pageBorder}`,
                      color: canAfford ? rar.color : GM.pageBorder,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap", opacity: canAfford ? 1 : 0.4,
                    }}>
                    {busy === p.id ? "..." : `\u25c6 ${p.cost.toLocaleString()}`}
                  </button>
                ) : (
                  <button
                    onClick={!isEq && busy !== p.id ? () => handleEquip(p) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: isEq ? "default" : "pointer",
                      background: isEq ? "rgba(232,184,75,0.06)" : "transparent",
                      border: `1px solid ${isEq ? GM.pageBorder : GM.inkGold + "66"}`,
                      color: isEq ? GM.vellumDim : GM.inkGoldBright,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}>
                    {isEq ? "WORN" : "EQUIP"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// COIN SKIN SHELF — T11, the third counter
// ─────────────────────────────────────────────────────────────
// The Signature counter's docstring promised "any future cool
// stuff gets shelved here too," and this is the third thing to
// take it up. The economy is byte-identical to the two shelves
// above on purpose: unlockCoinSkin -> atomic spendCoins +
// append-only unlock, GRANT-BEFORE-FLAG, live listeners so a
// purchase lands on screen without a refresh.
//
// The preview is a REAL coin — the same <CoinFaces> the
// Shatterfield renders at the top of a duel, standing still. Same
// rule as the pulse counter: nobody buys a swatch here.
//
// J'ynxi's Grin is cost 0 and therefore reads as owned. That is
// deliberate and it is a DIAL, not a law: she is the only mythic
// on the shelf, she should not be purchasable with coins, and
// until she has an earn path she is simply on the house. Give her
// one by moving the number, or by granting her from wherever she
// ought to come from.
// ─────────────────────────────────────────────────────────────
function CoinSkinShelf({ uid }) {
  const [coins, setCoins]       = useState(0);
  const [crests, setCrests]     = useState(0);   // ◆ — the mythic's lane
  const [unlocked, setUnlocked] = useState({});
  const [equipped, setEquipped] = useState(DEFAULT_COIN_SKIN);
  const [busy, setBusy]         = useState(null);
  const [note, setNote]         = useState(null);

  useEffect(() => {
    if (!uid) return;
    const offA = onValue(ref(db, `profiles/${uid}/chocolateCoins`),
      (s) => setCoins(s.val() || 0));
    const offC = onValue(ref(db, `profiles/${uid}/divineCrests`),
      (s) => setCrests(s.val() || 0));
    const offB = onValue(ref(db, `profiles/${uid}/coinSkin`),
      (s) => {
        const v = s.val() || {};
        setUnlocked(v.unlocked || {});
        setEquipped(typeof v.equipped === "string" && v.equipped ? v.equipped : DEFAULT_COIN_SKIN);
      });
    return () => { offA(); offB(); offC(); };
  }, [uid]);

  const isOwned = (k) => k.cost === 0 || !!unlocked[k.id];
  // The lane comes from the catalog, never from this component's idea
  // of what a rarity ought to cost.
  const laneOf = (k) => coinSkinCurrency(k.id);
  const walletFor = (k) => (laneOf(k) === "DC" ? crests : coins);

  const handleBuy = async (k) => {
    if (!uid || busy) return;
    setBusy(k.id); setNote(null);
    const res = await unlockCoinSkin(uid, k.id, k.cost, "emporium_coin", laneOf(k));
    if (res.ok) {
      setNote({ text: `\u25c6 ${k.name} struck. Equip it and it calls your next Solrend.`,
                color: SKIN_RARITY_META[k.rarity]?.color || GM.inkGold });
    } else if (res.reason === "insufficient-crests") {
      setNote({ text: `Not enough Divine Crests for ${k.name}. The Well at the Pier trades for them.`, color: "#f43f5e" });
    } else if (res.reason === "insufficient-coins") {
      setNote({ text: `Not enough Chocolate Coins for ${k.name}.`, color: "#f43f5e" });
    } else {
      setNote({ text: "The counter jammed \u2014 nothing was charged. Try again.", color: "#f43f5e" });
    }
    setBusy(null);
  };

  const handleEquip = async (k) => {
    if (!uid || busy) return;
    setBusy(k.id);
    await equipCoinSkin(uid, k.id);
    setBusy(null);
  };

  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ height: 1, background: GM.pageBorder, marginBottom: 18 }} />
      <div style={{
        fontSize: 10, letterSpacing: 4, color: GM.inkGoldBright,
        fontWeight: 900, fontFamily: GM.font, marginBottom: 6,
        textShadow: textGlow(GM.inkGoldBright),
      }}>✦ COIN SKINS</div>
      <div style={{
        fontSize: 11, color: GM.vellumFaint, marginBottom: 14,
        lineHeight: 1.7, fontStyle: "italic",
      }}>
        Every Solrend opens on a coin. It decides who moves first and it has
        never once cared what it looks like — so make it look like something.
        The flip stays honest whatever face you put on it.
      </div>

      {note && (
        <div style={{
          padding: "8px 14px", marginBottom: 12, fontSize: 11.5,
          fontStyle: "italic", color: note.color, textAlign: "center", letterSpacing: 1,
        }}>{note.text}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 30 }}>
        {coinShelfOrder().map(k => {
          const rar = SKIN_RARITY_META[k.rarity] || SKIN_RARITY_META.fable;
          const owned = isOwned(k);
          const isEq = equipped === k.id;
          const lane = laneOf(k);
          const canAfford = walletFor(k) >= k.cost;
          return (
            <div key={k.id} style={{
              border: `1px solid ${isEq ? rar.color : GM.pageBorder}`,
              borderRadius: 12, overflow: "hidden",
              background: OBSIDIAN_SLAB,
              boxShadow: isEq ? `0 0 16px ${rar.color}33, ${SHADOW_RAISED}` : SHADOW_RAISED,
            }}>
              {/* LIVE PREVIEW — both faces, standing still */}
              <div style={{
                position: "relative", padding: "18px 12px 16px",
                display: "flex", justifyContent: "center", gap: 26,
                background: `radial-gradient(ellipse at 50% 55%, ${rar.color}12, transparent 70%)`,
              }}>
                {["heads", "tails"].map(side => (
                  <div key={side} style={{ width: 76, height: 76, position: "relative" }}>
                    {/* `flat` — a shelf shows both faces at once, so the
                        3D coin's backface hiding and tails pre-rotation
                        are both dropped rather than cancelled. See the
                        note on CoinFaces; cancelling does not work. */}
                    <CoinFaces skinId={k.id} side={side} size={76} flat />
                  </div>
                ))}
                <div style={{
                  position: "absolute", top: 8, left: 10,
                  fontSize: 8, fontWeight: 900, letterSpacing: 2,
                  color: rar.color, textShadow: "0 1px 3px #000",
                }}>{rar.label}</div>
                {isEq && (
                  <div style={{
                    position: "absolute", top: 8, right: 10,
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: rar.color, textShadow: "0 1px 3px #000",
                  }}>◆ EQUIPPED</div>
                )}
              </div>

              <div style={{ padding: "10px 12px 12px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 900, fontStyle: "italic",
                    color: GM.vellum, fontFamily: GM.font, textShadow: TEXT_SET,
                  }}>{k.name}</div>
                  <div style={{
                    fontSize: 10, fontStyle: "italic", color: GM.vellumFaint,
                    marginTop: 2, lineHeight: 1.45,
                  }}>{k.blurb}</div>
                </div>
                {!owned ? (
                  <button
                    onClick={canAfford && busy !== k.id ? () => handleBuy(k) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: canAfford ? "pointer" : "not-allowed",
                      background: canAfford ? OBSIDIAN_SLAB_LIT : "transparent",
                      border: `1px solid ${canAfford ? rar.color : GM.pageBorder}`,
                      color: canAfford ? rar.color : GM.pageBorder,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap", opacity: canAfford ? 1 : 0.4,
                    }}>
                    {busy === k.id ? "..." : (
                      <>{lane === "DC" ? <DivineCrest size={11} /> : <ChocolateCoin size={11} />} {k.cost.toLocaleString()}</>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={!isEq && busy !== k.id ? () => handleEquip(k) : undefined}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      cursor: isEq ? "default" : "pointer",
                      background: isEq ? "rgba(232,184,75,0.06)" : "transparent",
                      border: `1px solid ${isEq ? GM.pageBorder : GM.inkGold + "66"}`,
                      color: isEq ? GM.vellumDim : GM.inkGoldBright,
                      fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}>
                    {isEq ? "WORN" : "EQUIP"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// VENUS — THE KEEPER OF THE COUNTER
// ─────────────────────────────────────────────────────────────
// caP'n, 2026-08-18: the shop's keeper has a name now. "Tiny's" was a
// default that stuck; she is VENUS, and the roadmap asks for the same
// treatment V'yen got — "her on screen beside the real purchasable
// packs."
//
// She does not replace the Emporium. The Emporium is a ROOM on the
// canonical stage rectangle (C5, The Treatment) and every shelf, tab
// and preview modal is measured against that rect; rebuilding them
// inside a dialogue well would be a regression wearing a feature's
// coat. So Venus STANDS BESIDE the room, in the letterbox margin the
// stage already leaves empty. Below the width where that margin
// exists she steps back and speaks from the header instead — a phone
// player is still served by Venus, she just cannot stand up at 390px.
//
// HER VOICE, from her own three books in Willee's Library: of Mystic
// Allie's infinite assistants only Venus earned a NAME. She remembers
// your favourite pack art. She holds holos under the counter for kids
// saving up. Undeath did not take her kindness, it gave it a longer
// shift. So she is WARM, and she is TIRED, and she is never a
// salesperson — the shop is already selling, she does not need to.
//
// 🟠 hinted, never stated: what she did with the assistant whose name
// it was. One line in twenty, and it is a joke she does not explain.
// ════════════════════════════════════════════════════════════════
// THE THREE AISLES — the 4xS pass on the counter (2026-08-22)
// ════════════════════════════════════════════════════════════════
// caP'n: *"apply some 4xS to the booster shop so it's organized but we
// don't want a million tabs to have to choose from either to get to
// where we want."*
//
// WHAT WAS WRONG. The shop had SIX top-level tabs — Packs, Starters,
// Foil Vault, Equipment, Companions, Signature — and the Packs tab
// then opened SIX MORE. Twelve flat destinations, two rails drawn in
// two different styles, and no relationship between them: "Companions"
// and "Foil Vault" sat at the same rank as "Packs", even though one is
// a whole department and the other is one shelf. Limited Edition was
// not on either rail at all; it was a strip bolted under the Packs
// info panel, so the rarest thing in the shop was the one thing with
// no door.
//
// ── 4xS, APPLIED ──────────────────────────────────────────────
// SIMPLE  — three aisles instead of six tabs. Not six renamed: three,
//           because there are genuinely three reasons to walk in here
//           (cards to open, a deck to play, a finish to own). The
//           second rail only exists inside an aisle that has more than
//           one shelf, so DECKS shows one rail and PACKS shows two —
//           you are never choosing from twelve things at once.
// SMART   — ONE table drives both levels and one <Rail> renders both.
//           Adding a shelf is one row here; it appears on the rail,
//           gets its Venus line, and routes. There is no second list
//           anywhere to forget. CATEGORY_DEFS still owns which pack
//           sits on which shelf and is read, not retyped.
// SPECIAL — each aisle carries its own colour and its own line of
//           Venus; a rail is not a generic control that got recoloured
//           three times. And Limited Edition is now a shelf with a
//           door like everything else.
// SEXY    — the two rails were already beautiful and were beautiful
//           DIFFERENTLY, which is what made the counter feel busy.
//           They are one component now; the aisle rail is the loud one
//           and the shelf rail is the quiet one, on purpose.
//
// ⚠ The shelf keys are what the body switches on. Renaming one without
// renaming its case below is an invisible empty aisle — the same class
// of failure as a scene on a dead node key.
const AISLES = [
  {
    key: "packs", label: "◆ PACKS", color: "#d4a84b",
    shelves: [
      // ── THE CORE, three drawers behind ONE DOOR ─────────────
      // ⭐ caP'n, 2026-08-22: *"the Class, Tomes, and Support [shelves]
      // will be bundled into a tab you must first click on called 'Core'
      // to expand and see the 3 options, that way it is 4xS."*
      //
      // `group: "core"` is the entire mechanism, and it is one field.
      // The rail collapses every shelf carrying the same group id behind
      // a single tab and opens the sub-rail when that tab is lit — see
      // <GroupedShelfRail>. These rows are otherwise UNCHANGED: they
      // still route, still carry their own Venus line, and if the group
      // field were deleted tomorrow they would simply be three tabs
      // again. That is deliberate; a grouping that rewrites its members
      // is a grouping you cannot undo.
      //
      // ⚠ DERIVED FROM CORE_DRAWERS, in its order. A fourth drawer added
      // there appears here, inside the Core door, with no edit at all —
      // which is the guarantee caP'n asked for: *"even when I make new
      // cards and we make new booster packs, they will still be Core."*
      ...Object.entries(CORE_DRAWERS).map(([key, d]) => ({
        key: `pk_${key}`, cat: key, label: d.label, venus: d.venus, group: "core",
      })),
      // ⚠ Foil, Activities, Core, Premier, Chaos and Limited had shelves here until
      // 2026-08-22. See the PLACERS banner on CATEGORY_DEFS for why
      // their category rows survive and their shelves do not.
    ],
  },
  {
    key: "decks", label: "⚔ DECKS", color: "#6ee7a8",
    shelves: [
      { key: "dk_starters",   label: "⚔ Starters",   cat: null, color: "#6ee7a8",
        name: "The Starter Shelf", subtitle: "A whole deck, as it was written", glyph: "⚔",
        venus: '"Everybody starts with one of these. Nobody stays with one."' },
      { key: "dk_companions", label: "✦ Companions", cat: null, color: "#a855f7",
        name: "The Menagerie", subtitle: "Something to walk in with", glyph: "✦",
        venus: '"Oh — bring them in next time. I keep something behind the counter."' },
    ],
  },
  {
    key: "vault", label: "✦ THE VAULT", color: "#ff66cc",
    shelves: [
      { key: "vt_foils",     label: "✦ Foils",     cat: null, color: "#ff66cc",
        name: "The Foil Vault", subtitle: "Any card, any finish", glyph: "✦",
        venus: '"That shelf is Crests only. I do not set the prices, I only dust them."' },
      { key: "vt_signature", label: "◆ Signature", cat: null, color: "#c084fc",
        name: "Signature & Flourish", subtitle: "Backdrops, pulses, coins", glyph: "◆",
        venus: '"Signed. By whom is a longer conversation than either of us has."' },
      { key: "vt_equipment", label: "🎩 Equipment", cat: null, color: "#60a5fa",
        name: "Equipment", subtitle: "Little things that matter", glyph: "🎩",
        venus: '"Little things. They matter more than the big ones, generally."' },
    ],
  },
  // ── EXPERIMENTAL — caP'n, 2026-09-02 (revised same day): the wing
  // stays VISIBLE but sells nothing. The Kyrium pilot's buy-flow was
  // reverted by caP'n's ruling — experimental cards live only in THE
  // FAUXDEX (Workshop → Codex door) as pure hypothesis until each is
  // individually canonized. This aisle is a signpost, not a shop.
  {
    key: "experimental", label: "✦ EXPERIMENTAL", color: "#4a2f7c",
    shelves: [
      { key: "ex_starters", label: "✦ New Arrivals", cat: null, color: "#4a2f7c",
        name: "The Experimental Wing", subtitle: "Look, don't touch — see the Fauxdex", glyph: "✦",
        venus: '"These haven\'t been blessed yet. Play them anyway — that\'s rather the point."' },
    ],
  },
];

// ── openingShelfOf — where an aisle drops you ────────────────────
// ⭐ 2026-08-22 — CLOSED IS THE CORRECT OPENING STATE, and it is the
// literal ask: *"a tab you must FIRST CLICK ON called 'Core' to expand
// and see the 3 options."* An aisle whose first shelf sits in a group
// hands back `null` — no shelf, door shut, nothing below the rail but
// the door and its line. Landing already inside School would make the
// Core tab a label you had to close, which is the opposite control.
const openingShelfOf = (aisleKey) => {
  const first = (AISLE_BY_KEY_RAW[aisleKey] || AISLES[0]).shelves[0];
  return first?.group ? null : first?.key || null;
};

const AISLE_BY_KEY_RAW = Object.fromEntries(AISLES.map(a => [a.key, a]));
const AISLE_BY_KEY = AISLE_BY_KEY_RAW;
const SHELVES = AISLES.flatMap(a => a.shelves.map(sh => ({ ...sh, aisle: a.key, aisleColor: a.color })));
const SHELF_BY_KEY = Object.fromEntries(SHELVES.map(sh => [sh.key, sh]));

// ── SHELF GROUPS — a door in front of a set of shelves ───────────
// ⭐ caP'n, 2026-08-22, on the Core: *"a tab you must first click on
// called 'Core' to expand and see the 3 options — that way it is 4xS."*
//
// ── WHY A GROUP AND NOT JUST A RENAMED SHELF ─────────────────────
// The three core drawers genuinely are three different shelves — a
// School pack and a Tome pack share nothing but a sigil and a price
// list. Merging them into one shelf would have been the SIMPLE-looking
// answer and the wrong one: you would scroll past eight Tome packs to
// reach Support. What was actually messy was the RAIL claiming there
// were three unrelated destinations when there is one destination with
// three drawers in it. So the shelves stay three and the door becomes
// one, which is the only arrangement that is honest about both.
//
// ── AND WHY IT IS A TABLE AND NOT AN `if (aisle === "packs")` ────
// A second group is now one row here. Hardcoding "core" into the rail
// would make the second group a rewrite of the rail, and the rail is
// the component this whole shop was collapsed INTO three months of
// tabs ago. One row, and it gets a door, a colour and a sub-rail.
const SHELF_GROUPS = {
  core: {
    label: "◈ Core", color: CORE_SIGIL.color,
    // The line under the sub-rail. It is doing real work: it is the one
    // place the shop states the promise the Core sigil is making.
    blurb: "School, Tomes and Support. The permanent spine — every pack in here, and every pack that ever joins it, deals cards stamped with the one Core sigil.",
  },
};

// ── <GroupedShelfRail> — the shelf rail, with doors ──────────────
// Renders a shelf rail where grouped shelves collapse behind one tab
// and expand into a quiet sub-rail when that tab is the live one.
// Ungrouped shelves are rendered exactly as before, beside the doors,
// so a mixed rail (a group + a loose "More" shelf) works with no
// special case — which is what the orphan net needs it to do.
//
// ⚠ THE ACTIVE GROUP IS DERIVED FROM THE ACTIVE SHELF, not held in its
// own state. A second piece of state here would be a second source of
// truth for "where am I", and the two would disagree the first time
// anything set the shelf from outside — which the aisle picker does on
// every single aisle change.
function GroupedShelfRail({ shelves, active, onPick }) {
  const groupOf = (sh) => sh.group || null;
  const activeShelf = shelves.find(sh => sh.key === active);
  const activeGroup = groupOf(activeShelf || {});

  // Doors and loose shelves, in the order the aisle declared them, each
  // group appearing at the position of its first member.
  const items = [];
  const seen = new Set();
  for (const sh of shelves) {
    const g = groupOf(sh);
    if (!g) { items.push({ ...sh, _kind: "shelf" }); continue; }
    if (seen.has(g)) continue;
    seen.add(g);
    const def = SHELF_GROUPS[g] || { label: g, color: null };
    items.push({ key: `grp_${g}`, label: def.label, color: def.color, _kind: "group", _group: g,
      _first: shelves.find(x => groupOf(x) === g)?.key });
  }

  const drawers = shelves.filter(sh => groupOf(sh) === activeGroup);
  const groupDef = activeGroup ? SHELF_GROUPS[activeGroup] : null;
  const hasDoor = items.some(it => it._kind === "group");

  // ⚠ THE ONE-BUTTON GUARD DOES NOT APPLY TO A DOOR, and getting this
  // wrong is how the Core tab failed to appear on its first run. The old
  // rule — "a rail with a single button on it is a button pretending to
  // be a choice" — is true of a SHELF rail and false of a DOOR: a shut
  // door is a control with two states, and suppressing it leaves the
  // drawers permanently open with nothing to close them, which is
  // exactly the screen caP'n asked to replace. So: suppress a lone
  // rail only when it is a lone SHELF.
  const showRail = items.length > 1 || hasDoor;

  return (
    <>
      {showRail && (
        <Rail
          items={items}
          active={activeGroup ? `grp_${activeGroup}` : active}
          onPick={(k) => {
            const it = items.find(x => x.key === k);
            if (it?._kind !== "group") { onPick(k); return; }
            // A door TOGGLES. Pressing the open one shuts it and returns
            // you to the counter — the same button, both ways, which is
            // the only behaviour that makes "click to expand" reversible.
            onPick(it._group === activeGroup ? null : it._first);
          }}
        />
      )}

      {/* THE DRAWERS — only once the door is open. */}
      {activeGroup && drawers.length > 1 && (
        <div style={{
          marginTop: -6, marginBottom: 14, padding: "9px 12px 4px",
          background: OBSIDIAN_RECESS,
          border: `1px solid ${(groupDef?.color || GM.inkGold)}33`,
          borderRadius: 10, boxShadow: SHADOW_RECESS,
          animation: "slideUp 0.18s ease-out both",
        }}>
          <Rail items={drawers} active={active} onPick={onPick} />
          {groupDef?.blurb && (
            <div style={{
              fontSize: 8.5, color: GM.vellumFaint, fontStyle: "italic",
              lineHeight: 1.6, letterSpacing: 0.3, marginTop: -8, paddingBottom: 4,
            }}>{groupDef.blurb}</div>
          )}
        </div>
      )}

      {/* THE SHUT DOOR. Not an empty screen — an invitation. Every group
          with a closed door says what is behind it, so the counter is
          never a rail floating over nothing. */}
      {!active && hasDoor && (
        <div style={{
          marginBottom: 16, padding: "18px 20px", textAlign: "center",
          background: OBSIDIAN_RECESS,
          border: `1px dashed ${GM.inkGold}44`,
          borderRadius: 12, boxShadow: SHADOW_RECESS,
          animation: "slideUp 0.2s ease-out both",
        }}>
          {items.filter(it => it._kind === "group").map(it => {
            const def = SHELF_GROUPS[it._group] || {};
            const c = def.color || GM.inkGoldBright;
            return (
              <div key={it.key} style={{ marginBottom: 2 }}>
                <div style={{
                  fontSize: 11, fontWeight: 900, letterSpacing: 2, color: c,
                  fontFamily: GM.font, textShadow: `0 0 10px ${c}55, 0 1px 2px #000`,
                }}>{def.label || it.label}</div>
                {def.blurb && (
                  <div style={{
                    fontSize: 9, color: GM.vellumFaint, fontStyle: "italic",
                    lineHeight: 1.65, marginTop: 6, maxWidth: 380,
                    marginLeft: "auto", marginRight: "auto",
                  }}>{def.blurb}</div>
                )}
                <div style={{
                  fontSize: 8, color: GM.vellumDim, letterSpacing: 1.6,
                  marginTop: 9, fontWeight: 900, fontFamily: GM.font,
                }}>◈ OPEN IT ABOVE</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── <Rail> — the ONE tab rail, worn two ways ─────────────────────
// `tone: "aisle"` is the loud one (the department you are in);
// `tone: "shelf"` is the quiet one (which shelf inside it). Two
// components that looked different for no reason are why the counter
// felt busy; one component with a tone is why it does not.
// ── <ShelfHead> — the header a shelf wears ───────────────────────
// PackShelf drew one of these for its categories and NOTHING else in
// the shop did, so five of the eleven shelves opened with no title,
// no subtitle and no count — you knew where you were only because a
// button was lit. Same header, every shelf, one component.
function ShelfHead({ def, count, noun = "item" }) {
  const c = def?.color || def?.aisleColor || GM.inkGoldBright;
  return (
    <div style={{ marginBottom: 12, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontSize: 12, color: c, fontWeight: 900, fontFamily: GM.font, letterSpacing: 2, textShadow: `0 0 10px ${c}66, 0 1px 2px #000` }}>
          {def?.glyph} {def?.name}
        </div>
        <div style={{ fontSize: 8, color: GM.vellumFaint, fontStyle: "italic", marginTop: 2, letterSpacing: 1 }}>
          {def?.subtitle}
        </div>
      </div>
      {count != null && (
        <div style={{ fontSize: 8, color: GM.vellumFaint, fontStyle: "italic" }}>
          {count} {count === 1 ? noun : `${noun}s`}
        </div>
      )}
    </div>
  );
}

function Rail({ items, active, onPick, tone = "shelf" }) {
  const aisle = tone === "aisle";
  return (
    <div style={{
      display: "flex", gap: aisle ? 6 : 7,
      marginBottom: aisle ? 12 : 16,
      overflowX: aisle ? "visible" : "auto",
      paddingBottom: aisle ? 0 : 6,
    }}>
      {items.map(it => {
        const on = active === it.key;
        const c = it.color || it.aisleColor || GM.inkGoldBright;
        return (
          <button key={it.key} onClick={() => onPick(it.key)} style={{
            flex: aisle ? 1 : "0 0 auto",
            padding: aisle ? "11px 2px" : "8px 13px",
            clipPath: facetTB(8), border: "none",
            borderTop: on ? `1px solid ${c}` : "1px solid transparent",
            background: on
              ? `linear-gradient(135deg, ${c}2e, transparent 60%), ${OBSIDIAN_SLAB_LIT}`
              : OBSIDIAN_SLAB,
            color: on ? "#fff" : GM.vellumFaint,
            boxShadow: on
              ? `inset 0 1px 0 ${c}aa, inset 0 0 ${aisle ? 22 : 18}px ${c}2a`
              : "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -5px 10px rgba(0,0,0,0.45)",
            filter: on ? `drop-shadow(0 0 ${aisle ? 10 : 8}px ${c}55)` : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
            fontFamily: GM.font, fontSize: aisle ? 10.5 : 9.5, fontWeight: 900,
            cursor: "pointer", letterSpacing: aisle ? 1.4 : 1, whiteSpace: "nowrap",
            textShadow: on ? `0 0 8px ${c}aa` : TEXT_SET,
            transition: "all 0.18s ease",
          }}>{it.label}</button>
        );
      })}
    </div>
  );
}

// Venus's counter lines live on the shelf rows above so that adding a
// shelf cannot leave her silent on it. This is kept only as the
// fallback for a caller that has no shelf yet.
const VENUS_DEFAULT = '"Take your time. They do not go stale."';

function venusLine(shelfKey, coins) {
  // The deep cut, rare and unexplained. Gated on nothing but the roll
  // so a first-day player can hit it and never see it again — which
  // is the only way a line like this stays a rumour.
  if (Math.random() < 0.04) {
    return '"...Sorry. You looked like somebody. It has been a long shift."';
  }
  if (coins <= 0) {
    return '"Window shopping is free. It is the only thing in here that is."';
  }
  return SHELF_BY_KEY[shelfKey]?.venus || VENUS_DEFAULT;
}

function PackShop({ profile, playerName, onSelectPack, onBack }) {
  const coins = profile?.chocolateCoins || 0;
  const [hoveredPack, setHoveredPack] = useState(null);
  const [previewPack, setPreviewPack] = useState(null);  // pack whose preview modal is open
  const [buying, setBuying] = useState(null);
  const [msg, setMsg] = useState("");
  // THE AISLE AND THE SHELF. Two pieces of state instead of one,
  // which is the only cost of the whole 4xS pass — and it buys back
  // the two useStates that PackShelf and the featured strip used to
  // hold privately. Changing aisle always lands you on that aisle's
  // FIRST shelf: a shop that remembers you were on "Equipment" and
  // drops you there when you asked for DECKS is a shop that argues
  // with you.
  const [aisle, setAisle] = useState("packs");
  // null = a group's door is shut and nothing is open below the rail.
  const [shelf, setShelf] = useState(() => openingShelfOf(AISLES[0].key));
  const pickAisle = (k) => {
    setAisle(k);
    setShelf(openingShelfOf(k));
    setMsg("");
  };
  // Memoised so the currentAisle memo below has a stable dep — an
  // array rebuilt every render would defeat it silently.
  const packCategories = useMemo(() => buildCategories(BOOSTER_PACKS), []);
  // ⚠ THE ORPHAN NET, RESTORED. buildCategories synthesizes a "more"
  // category for any pack with no CATEGORY_DEFS entry, and the OLD
  // PackShelf grew it a tab automatically because it built its rail
  // from the categories themselves. Moving the rail up to a written
  // table severed that: the next pack added to storyData.js without a
  // CATEGORY_DEFS row would have vanished from the shop entirely and
  // been unbuyable, silently — the exact failure buildCategories was
  // written to prevent. So the packs aisle's shelves are DERIVED, and
  // "More" appears the moment it has anything in it. Found by an
  // adversarial pass over this same commit.
  const currentAisle = useMemo(() => {
    const a = AISLE_BY_KEY[aisle] || AISLES[0];
    if (a.key !== "packs") return a;
    const orphans = packCategories.find(c => c.key === "more");
    if (!orphans) return a;
    return { ...a, shelves: [...a.shelves, {
      key: "pk_more", label: "◆ More", cat: "more", aisle: "packs", aisleColor: a.color,
      venus: '"Those came in without paperwork. They are still cards."',
    }] };
  }, [aisle, packCategories]);
  // ⚠ `shelf` is now legitimately null (a shut door), so this may be
  // undefined and EVERY reader below must be optional. It used to fall
  // back to SHELVES[0], which would have quietly drawn the Starter shelf
  // under the Packs aisle the moment the Core door was closed.
  const shelfDef = shelf
    ? (SHELF_BY_KEY[shelf] || currentAisle.shelves.find(sh => sh.key === shelf))
    : null;
  const shelfCat = shelfDef?.cat ? packCategories.find(c => c.key === shelfDef.cat) : null;
  useModalKeys(previewPack ? () => setPreviewPack(null) : onBack);

  // Same shape as the Vault buy on purpose — the shop already
  // had one buy-flow idiom and a second one that read differently
  // would be a third thing to keep in step. progression.buyStarterDeck
  // owns the money and the cards; this owns only the saying-so.
  const handleBuyStarter = async (deck) => {
    if (coins < STARTER_DECK_COST) { setMsg("Not enough Chocolate Coins."); return; }
    setBuying(deck.id);
    try {
      const { buyStarterDeck } = await import("./progression");
      const res = await buyStarterDeck(playerName, deck.id);
      setMsg(res.ok
        ? `⚔ ${deck.name} forged — ${res.poured} cards in your Collection.`
        : (res.why || "The forge went cold."));
    } catch (e) {
      setMsg("Purchase failed.");
    }
    setBuying(null);
  };

  // ⚠ handleBuyFeatured removed 2026-08-22 with the Limited shelf.
  // handleBuyFoil below is the idiom it was written to match, and it
  // is still the shop's one buy-flow shape.



  const handleBuyEquipment = async (item) => {
    if (coins < item.cost) { setMsg("Not enough Chocolate Coins."); return; }
    setBuying(item.id);
    try {
      const { spendCoins, addCardToCollection } = await import("./progression");
      const spent = await spendCoins(playerName, item.cost);
      if (spent) {
        await addCardToCollection(playerName, item.curio, false, null, "emporium");
        setMsg(`✦ ${item.name} purchased! ${getCard(item.curio)?.name} added to your collection.`);
      } else { setMsg("Not enough Chocolate Coins."); }
    } catch(e) { setMsg("Purchase failed. Try again."); }
    setBuying(null);
  };

  const handleBuyFoil = async (entry, chosenTier) => {
    const tier = chosenTier || entry.tier;
    const cost = getFoilVaultCost(entry, tier);
    // ◆ the reserve, not the pocket money. The real refusal lives at the
    // write (progression.buyFoilSingle) — this one only saves a round
    // trip, and it deliberately does not duplicate the one-per-account
    // rule, because a rule enforced in two places is a rule that drifts.
    const crests = profile?.divineCrests || 0;
    if (crests < cost) { setMsg("Not enough Divine Crests. The Well at the Pier trades for them."); return; }
    setBuying(entry.id);
    try {
      const result = await buyFoilSingle(playerName, entry.cardId, tier, cost);
      if (result.success) {
        const info = HOLO_TIER_INFO[tier];
        const card = getCard(entry.cardId);
        setMsg(`✦ ${card?.name || entry.cardId} — ${info?.label} foil secured! Check your Collection.`);
      } else {
        setMsg(result.reason || "Purchase failed. Try again.");
      }
    } catch(e) { setMsg("Purchase failed. Try again."); }
    setBuying(null);
  };

  // ── CANONICAL STAGE — the Emporium now lives inside the ONE
  //    room rectangle (Workshop geometry). Root is transparent so
  //    the equipped backdrop letterboxes around it via RoomOverlay's
  //    LetterboxFrame, exactly like every other room. Content
  //    scrolls INSIDE the rectangle.
  const stage = useStageRect();

  // Whether there is genuinely margin for her to stand in. Same
  // measurement KeeperMargin makes internally, asked here so the
  // header knows to speak for her when there is not.
  const venusStanding = true;   // she always stands now — on the NpcStage, left (2026-09-23)
  void stage;
  // ⚠ MEMOISED, and it has to be. venusLine() rolls for its rare
  // line, and this component re-renders on every pack hover — an
  // un-memoised call would re-roll her voice several times a second
  // and she would stutter through six different sentences while you
  // moved the mouse across one shelf. The dependency is the TAB and
  // whether you have any money, because those are the only two
  // things she is actually reacting to.
  const venusSays = useMemo(() => venusLine(shelf, coins), [shelf, coins > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
    {/* ⭐ VENUS'S COUNTER IS A CONVERSATION (caP'n, 2026-09-23): "whenever
        npc's and the player are talking … the backdrop and menu should be
        gone, only a back button." The shop now stands on the NpcStage like
        V'yen and Willy — Venus in 3D on the left, you on the right, the
        shelves in the well. No room backdrop, no chrome rail (App.js no
        longer wraps this screen in RoomOverlay); ← Back is the way out.
        No onClose: useModalKeys already owns Escape here, and two owners
        would goBack() twice. */}
    <NpcStage keeperId="venus" badge="Card Packs" line={venusSays} profile={profile} backdrop="plaza_1.webp">
      <div style={{ width: "100%", maxWidth: 480, padding: "0 16px", zIndex: 1, flex: 1 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0 6px" }}>
          <button onClick={onBack} style={{
            background: OBSIDIAN_SLAB, border: "none",
            borderTop: "1px solid rgba(226,232,244,0.2)",
            clipPath: facetTB(8),
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -4px 8px rgba(0,0,0,0.4)",
            filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.55))",
            color: GM.vellumDim, padding: "7px 15px",
            cursor: "pointer", fontFamily: GM.font, fontSize: 12, textShadow: TEXT_SET,
          }}>← Back</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              fontSize: 18, fontWeight: 900, fontStyle: "italic",
              color: "#f0cc60", letterSpacing: 4, fontFamily: GM.font,
              textShadow: textGlow(GM.inkGoldBright),
            }}>◆ Divine Shop ◆</div>
            <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 4, textShadow: TEXT_SET }}>THE UNSTABLE EMPORIUM · VENUS'S COUNTER</div>
          </div>
          {/* ◕ Coin counter — a recessed gem well in a forged-gold frame */}
          <div style={{
            padding: 2, clipPath: facet(8), background: GOLD_METAL,
            filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))", flexShrink: 0,
          }}>
            <div style={{
              clipPath: facet(7), background: OBSIDIAN_RECESS,
              boxShadow: SHADOW_RECESS,
              display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
            }}>
              <ChocolateCoin size={13} />
              <span style={{ fontSize: 13, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGoldBright) }}>{coins.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Metal rule under the title */}
        <div style={{ height: 2, margin: "0 auto 14px", width: "70%", background: GOLD_METAL_H, boxShadow: `0 0 8px ${GM.inkGoldBright}55` }} />

        {/* Venus, speaking from the counter on the widths where there
            is no margin for her to stand in. NOT a duplicate of the
            figure's plate — it is the same line, in the only place
            left to put it, and it is why a phone still gets a keeper
            instead of a vending machine. */}
        {!venusStanding && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            margin: "0 0 14px", padding: "8px 12px",
            background: OBSIDIAN_SLAB, clipPath: facetTB(8),
            borderTop: `1px solid ${GM.inkGold}66`,
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.10), inset 0 -4px 9px rgba(0,0,0,0.45)",
          }}>
            <span style={{ fontSize: 8, letterSpacing: 3, fontWeight: 900, color: GM.inkGoldBright, flexShrink: 0, textShadow: TEXT_SET }}>VENUS</span>
            <span style={{ fontSize: 10.5, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.55 }}>{venusSays}</span>
          </div>
        )}

        {/* ── THE AISLE RAIL — three departments ── */}
        <Rail items={AISLES} active={aisle} onPick={pickAisle} tone="aisle" />

        {/* ── THE SHELF RAIL — only when the aisle HAS more than one.
            A rail with a single button on it is a button pretending to
            be a choice. All three aisles currently clear that bar, so
            today this guard suppresses nothing; it exists because an
            aisle is one deletion away from being a single shelf and a
            lone lit button is worse than no rail. */}
        {currentAisle.shelves.length > 1 && (
          <GroupedShelfRail
            shelves={currentAisle.shelves}
            active={shelf}
            onPick={(k) => { setShelf(k); setMsg(""); }}
          />
        )}

        {msg && (
          <div style={{
            background: `linear-gradient(135deg, ${GM.inkGold}12, transparent 55%), ${OBSIDIAN_SLAB}`,
            border: `1px solid ${GM.inkGold}`,
            boxShadow: `0 0 16px ${GM.inkGold}2a, ${SHADOW_RAISED}`,
            color: GM.inkGoldBright, padding: "10px 16px", borderRadius: 8,
            fontSize: 12, marginBottom: 14, textAlign: "center", fontStyle: "italic", fontFamily: GM.font,
            textShadow: TEXT_SET, animation: "slideUp 0.25s ease-out both",
          }}>{msg}</div>
        )}

        {/* ── THE PACK SHELVES ──────────────────────────────────
            One block for six of the seven shelves, because they are
            the same object with a different category — the vitrine,
            the pedestals, the info panel underneath. Limited Edition
            is the seventh and is genuinely different (it sells named
            cards, not sealed packs), so it gets its own block rather
            than a flag threaded through this one. THE EXCEPTION LAW:
            the exception is written out, not smoothed away. */}
        {shelfCat && (
          <>
            <ShelfHead
              def={{ ...shelfDef, name: shelfCat.name, subtitle: shelfCat.subtitle, glyph: shelfCat.glyph, color: shelfCat.color }}
              count={shelfCat.packs?.length || 0}
              noun="pack"
            />
            <PackShelf
              category={shelfCat}
              coins={coins}
              onSelectPack={setPreviewPack}
              selectedId={hoveredPack?.id}
              onHover={setHoveredPack}
            />
            <div style={{ marginTop: 20 }}>
              <PackInfoPanel
                pack={hoveredPack}
                canAfford={hoveredPack ? coins >= hoveredPack.cost : false}
                onBuy={() => hoveredPack && setPreviewPack(hoveredPack)}
              />
            </div>
          </>
        )}

        {/* ⚠ LIMITED EDITION WAS HERE AND IS GONE (2026-08-22).
            caP'n deleted the section along with Core, Premier and
            Chaos: *"right now I am looking at it and there is just way
            too much going on."* FEATURED_HOLOS still exists in
            storyData and both its listings are Foil Vault overrides, so
            nothing became unbuyable — `aff_evil` in Khao and
            `spectral_tiger_lord` in Solrend are both still on the Vault
            shelf for Divine Crests. What DID go with it is the only
            route that stamped a card with the `limited` provenance
            sigil; that sigil is now unreachable and stays defined so
            existing cards still render their mark. See the foil ledger
            in TERROR_FOIL_LEDGER.md. */}

        {/* ── THE FOIL VAULT ── */}
        {shelf === "vt_foils" && (
          <>
            <ShelfHead def={shelfDef} count={FOIL_VAULT?.length || 0} noun="card" />
            <FoilVault profile={profile} onBuy={handleBuyFoil} busyId={buying} uid={profile?.uid || playerName} onMessage={setMsg} />
          </>
        )}

        {/* ── EQUIPMENT ── */}
        {shelf === "vt_equipment" && (
          <div>
            <ShelfHead def={shelfDef} count={EQUIPMENT_ITEMS?.length || 0} />
            <div style={{
              fontSize: 11, color: GM.vellumFaint, marginBottom: 16,
              lineHeight: 1.7, fontStyle: "italic",
            }}>
              Purchase equipment to unlock its linked Curio card for your duels.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {EQUIPMENT_ITEMS.map(item => {
                const owned = profile?.collection?.[item.curio]?.count > 0;
                const canAfford = coins >= item.cost;
                return (
                  <div key={item.id} style={{
                    background: OBSIDIAN_SLAB,
                    border: `1px solid ${owned ? GM.inkGold : GM.pageBorder}`,
                    borderRadius: 12, padding: "14px 16px",
                    boxShadow: owned ? `0 0 14px ${GM.inkGold}22, ${SHADOW_RAISED}` : SHADOW_RAISED,
                    display: "flex", alignItems: "center", gap: 14,
                  }}>
                    <div style={{ fontSize: 32, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }}>{item.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                        <span style={{
                          fontSize: 13, fontWeight: 900, fontStyle: "italic",
                          color: owned ? GM.inkGold : GM.vellumDim, fontFamily: GM.font, textShadow: TEXT_SET,
                        }}>{item.name}</span>
                        {owned && <span style={{
                          fontSize: 9, color: GM.inkGold,
                          border: `1px solid ${GM.inkGold}`, borderRadius: 10, padding: "1px 6px",
                        }}>OWNED</span>}
                      </div>
                      <div style={{ fontSize: 10, color: GM.vellumFaint }}>{item.description}</div>
                      <div style={{ fontSize: 9, color: GM.inkGold, marginTop: 3 }}>
                        ✦ Unlocks: {getCard(item.curio)?.name}
                      </div>
                    </div>
                    {!owned ? (
                      <button
                        onClick={canAfford && !buying ? () => handleBuyEquipment(item) : undefined}
                        style={{
                          padding: "8px 14px",
                          background: canAfford ? OBSIDIAN_SLAB_LIT : "transparent",
                          border: `1px solid ${canAfford ? GM.inkGold : GM.pageBorder}`,
                          borderRadius: 8, cursor: canAfford ? "pointer" : "not-allowed",
                          color: canAfford ? GM.inkGold : GM.pageBorder,
                          boxShadow: canAfford ? "inset 0 1px 0 rgba(245,216,120,0.25)" : "none",
                          fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                          whiteSpace: "nowrap", opacity: canAfford ? 1 : 0.4,
                        }}>
                        {buying === item.id ? "..." : `◆ ${item.cost}`}
                      </button>
                    ) : <span style={{ fontSize: 20, color: GM.inkGold, textShadow: `0 0 8px ${GM.inkGold}` }}>✦</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SIGNATURE — backdrops & future flourishes ── */}
        {shelf === "vt_signature" && (
          <>
            <ShelfHead def={shelfDef} count={null} />
            <SignatureShelf uid={playerName} />
            <DrawPulseShelf uid={playerName} />
            <CoinSkinShelf uid={playerName} />
          </>
        )}

        {/* ── STARTERS TAB — the whole shelf, rendered ──────────
            caP'n: "add all starter decks to the booster shop and
            make a starter deck section so when I make more."

            "SO WHEN I MAKE MORE" is the spec, so there is no list
            here. This maps STARTER_DECKS. A thirteenth deck shows
            up in the shop the moment it is authored in
            starterDecks.js and nothing in this file is touched. */}
        {shelf === "dk_starters" && (
          <div>
            <ShelfHead def={shelfDef} count={STARTER_DECKS.filter((d) => !d.experimental).length} noun="deck" />
            <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic",
              letterSpacing: 1, lineHeight: 1.7, marginBottom: 16, maxWidth: 620 }}>
              A whole deck, as it was written — both lanes, its Curios and its Prophecy.
              Bought or granted, the cards wear the same Starter mark; the shelf is a second
              door to the same place, not a second origin. Buy one twice if you want the copies.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {STARTER_DECKS.filter((deck) => !deck.experimental).map((deck) => {
                const total = starterDeckContents(deck).length;
                const afford = coins >= STARTER_DECK_COST;
                const busy = buying === deck.id;
                return (
                  <div key={deck.id} style={{
                    // ONE PER ROW, FILLING THE COLUMN — and that is the
                    // right answer, not a compromise. The shop's content
                    // column measures ~416px live, so two of these can
                    // never sit side by side no matter how the basis is
                    // tuned. The Packs tab beside it runs three across
                    // because its tiles are WORDLESS art squares; a
                    // starter card carries a three-line description, and
                    // at ~200px that paragraph becomes six ragged lines.
                    //
                    // The original maxWidth:340 left ~80px of dead space
                    // to the right of every card inside a column that was
                    // already narrow. This fills it. Measured in the
                    // browser, not guessed — the first attempt at this
                    // was aimed at getting two across and was simply
                    // wrong about the width available.
                    flex: "1 1 220px", maxWidth: 400, padding: "14px 16px 16px",
                    background: OBSIDIAN_SLAB, clipPath: facetTB(10), border: "none",
                    borderTop: `1px solid ${deck.color || GM.inkGold}88`,
                    boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 9px rgba(0,0,0,0.45)",
                    filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))",
                  }}>
                    {/* ⭐ THE DECK BOX. caP'n, 2026-08-22: "need
                        pictures where we can — starter decks have none
                        — need it absolutely professional Illegal
                        Empire AAA quality." This tile used to open
                        with a NINE-PIXEL DOT while the Packs shelf
                        next door rendered wrappers. See StarterArt.js
                        for why it is a deck and not a tinted wrapper.
                        The art sits LEFT of the text rather than above
                        it because a starter card carries three lines
                        of description and stacking them would double
                        the tile's height for no reading gained. */}
                    <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                      <StarterArt
                        deck={deck}
                        w={74}
                        h={104}
                        foil={afford}
                        dim={afford ? 1 : 0.5}
                        style={{ flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontStyle: "italic", color: "#ffe9a8",
                          textShadow: textGlow(GM.inkGoldBright), letterSpacing: 1 }}>{deck.name}</div>
                        <div style={{ fontSize: 8, letterSpacing: 4, color: GM.vellumFaint, marginTop: 5 }}>
                          {(deck.school || "").toUpperCase()} · {total} CARDS
                        </div>
                        <div style={{ fontSize: 10.5, color: GM.vellumDim, lineHeight: 1.55, marginTop: 8 }}>
                          {deck.description}
                        </div>
                      </div>
                    </div>
                    <button
                      disabled={!afford || busy}
                      onClick={() => handleBuyStarter(deck)}
                      style={{
                        marginTop: 12, width: "100%", padding: "9px 0",
                        border: "none", clipPath: facetTB(7),
                        borderTop: afford ? "1px solid #f5d878" : "1px solid transparent",
                        background: afford
                          ? `linear-gradient(135deg, ${GM.inkGold}, ${GM.inkGoldDim})`
                          : OBSIDIAN_SLAB,
                        color: afford ? GM.page : GM.vellumFaint,
                        cursor: (afford && !busy) ? "pointer" : "default",
                        fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                        fontStyle: "italic", letterSpacing: 1,
                      }}>
                      {busy ? "Forging…" : `◕ ${STARTER_DECK_COST}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {shelf === "ex_starters" && (
          <div>
            <ShelfHead def={shelfDef} count={null} />
            {/* ── THE FAUXDEX POINTER — caP'n, 2026-09-02: the Experimental
                wing is announced here but sells NOTHING. Every experimental
                card was pulled from the live engine the same day it piloted
                (Kyrium included) — they are hypothesis, not product, and
                they are viewable only in THE FAUXDEX (Willie's Workshop →
                the Codex door → "The Fauxdex"). No buy flow, no
                STARTER_DECKS filter, no imports from the experimental
                pocket file — this shelf must stay inert so the system
                can never get confused about what is real. */}
            <div style={{
              maxWidth: 620, padding: "22px 20px",
              background: OBSIDIAN_SLAB, clipPath: facetTB(10),
              borderTop: "1px solid #4a2f7c",
              boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 9px rgba(0,0,0,0.45)",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))",
            }}>
              <div style={{ fontSize: 15, fontStyle: "italic", color: "#c9b3ff",
                textShadow: textGlow("#8a5fd8"), letterSpacing: 1.5 }}>
                ✦ The Experimental Wing
              </div>
              <div style={{ fontSize: 8, letterSpacing: 4, color: GM.vellumFaint, marginTop: 6 }}>
                NOTHING FOR SALE · NOTHING PLAYABLE · EVERYTHING IMAGINED
              </div>
              <div style={{ fontSize: 10.5, color: GM.vellumDim, lineHeight: 1.7, marginTop: 12 }}>
                New Ilks, new Classes, whole new Schools — hundreds of cards, all of
                them still wet ink. None can be bought, owned, or played yet. When one
                is ready, it earns its blessing and walks out of this wing for good.
              </div>
              <div style={{ marginTop: 14, padding: "9px 12px", clipPath: facetTB(7),
                background: "rgba(74,47,124,0.22)", borderTop: "1px solid #8a5fd866",
                fontSize: 10, color: "#c9b3ff", letterSpacing: 1.5, fontStyle: "italic" }}>
                ◆ View them all in THE FAUXDEX — Willie's Workshop, at the Codex door.
              </div>
            </div>
          </div>
        )}

        
        {/* ── COMPANIONS — the Menagerie ── */}
        {shelf === "dk_companions" && (
          <>
          <ShelfHead def={shelfDef} count={null} />
          <CompanionMenagerie
            profile={profile}
            onRefresh={() => {
              // Profile is listened to live in Plaza — no action needed here,
              // but this prop exists if a future context needs manual refresh.
            }}
          />
          </>
        )}

      </div>
    </NpcStage>

      {/* Pack preview modal — OUTSIDE the stage: the well is transformed,
          and a position:fixed modal inside a transform is pinned to it */}
      {previewPack && (
        <PackPreviewModal
          pack={previewPack}
          canAfford={coins >= previewPack.cost}
          onConfirm={(pack) => { setPreviewPack(null); onSelectPack(pack); }}
          onClose={() => setPreviewPack(null)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN BOOSTER PACK COMPONENT
// ─────────────────────────────────────────────────────────────
export default function BoosterPack({ pack, cards, playerName, onClose, onOpenAnother }) {
  const [phase, setPhase] = useState("pack");
  const [cracking, setCracking] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const [showHoloReveal, setShowHoloReveal] = useState(null);
  const [burstActive, setBurstActive] = useState(false);
  // T21 — pulls scrapped at the reveal, by slot index: { [i]: paid◕ }.
  const [scrappedAt, setScrappedAt] = useState({});

  // Escape: close holo reveal if open, else close pack
  useModalKeys(showHoloReveal ? () => setShowHoloReveal(null) : onClose, { enabled: phase === "done" || !!showHoloReveal });
  const [allRevealed, setAllRevealed] = useState(false);

  const holoCards = cards?.filter(c => c.isHolo) || [];
  const pendingHolos = useRef([...holoCards]);

  const handlePackTap = () => {
    if (phase !== "pack") return;
    setCracking(true);
    setTimeout(() => {
      setBurstActive(true);
      setPhase("revealing");
      setTimeout(() => setBurstActive(false), 800);
    }, 500);
  };

  const handleRevealCard = (index) => {
    // Already revealed → reopen the big viewer for ANY card (plain cards
    // included) — every pull is inspectable, sigil and all.
    if (index < revealedCount) { setShowHoloReveal(cards[index]); return; }
    if (index !== revealedCount) return; // must reveal in order
    const card = cards[index];
    setRevealedCount(index + 1);

    // If holo — show full screen reveal after a beat
    if (card.isHolo) {
      setTimeout(() => setShowHoloReveal(card), 300);
    }

    if (index + 1 >= cards.length) {
      setTimeout(() => setAllRevealed(true), 400);
    }
  };

  const handleRevealAll = () => {
    setRevealedCount(cards.length);
    setAllRevealed(true);
  };

  if (!pack || !cards) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000008", zIndex: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: GM.font, overflow: "hidden" }}>

      {/* The ritual chamber breathes — swirl in the pack's own color */}
      <SwirlField accent={pack.accentColor} accent2={GM.amethyst} embers={false} />

      {/* Background glow */}
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 40%, ${pack.accentColor}18, transparent 70%)`, pointerEvents: "none" }} />

      {/* Particle burst */}
      <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)" }}>
        <ParticleBurst color={pack.accentColor} active={burstActive} />
      </div>

      {/* ── PHASE: PACK ── */}
      {phase === "pack" && (
        <div style={{ animation: "slideUp 0.5s ease-out both", zIndex: 1 }}>
          <PackDisplay pack={pack} onTap={handlePackTap} cracking={cracking} />
        </div>
      )}

      {/* ── PHASE: REVEALING ── */}
      {phase === "revealing" && (
        <div style={{ width: "100%", maxWidth: 500, padding: "0 16px", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic", color: pack.accentColor, letterSpacing: 3, fontFamily: GM.font, textShadow: `0 0 12px ${pack.accentColor}77, 0 1px 2px #000` }}>
              {pack.name}
            </div>
            <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 4, textShadow: TEXT_SET }}>
              {revealedCount < cards.length
                ? `Tap each card to reveal — ${cards.length - revealedCount} remaining`
                : "All cards revealed!"}
            </div>
          </div>

          {/* Cards row */}
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
            {cards.map((card, i) => (
              <RevealedCard
                key={i}
                cardResult={card}
                index={i}
                isRevealed={i < revealedCount}
                onClick={() => handleRevealCard(i)}
                scrappedPay={scrappedAt[i] ?? null}
              />
            ))}
          </div>

          {/* Actions */}
          {!allRevealed && revealedCount < cards.length && (
            <button onClick={handleRevealAll}
              style={{ display: "block", margin: "0 auto", padding: "8px 24px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 20, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 11 }}>
              Reveal All
            </button>
          )}

          {allRevealed && (
            <div style={{ animation: "slideUp 0.4s ease-out both", display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
              {/* Summary */}
              <div style={{ background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`, borderRadius: 12, padding: "14px 18px", textAlign: "center", boxShadow: SHADOW_RAISED }}>
                <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 3, marginBottom: 8, textShadow: TEXT_SET }}>◆ PACK SUMMARY</div>
                <div style={{ display: "flex", justifyContent: "center", gap: 24 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellumDim, fontFamily: GM.fontUI }}>{cards.filter(c => !c.isHolo).length}</div>
                    <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2 }}>CARDS</div>
                  </div>
                  {holoCards.length > 0 && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: GM.inkGold, fontFamily: GM.fontUI, textShadow: `0 0 10px ${GM.inkGold}55` }}>{holoCards.length}</div>
                      <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2 }}>HOLO ✦</div>
                    </div>
                  )}
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: GM.inkGold, fontFamily: GM.fontUI }}>{cards.length}</div>
                    <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2 }}>TOTAL</div>
                  </div>
                </div>
              </div>

              <button onClick={onOpenAnother} style={{ width: "100%", padding: "14px", background: OBSIDIAN_SLAB_LIT, border: `1px solid ${pack.accentColor}`, borderRadius: 10, color: pack.accentColor, cursor: "pointer", fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic", boxShadow: shimmerBorder(pack.accentColor), transition: "all 0.2s" }}>
                ◆ Open Another Pack
              </button>
              <button onClick={onClose} style={{ width: "100%", padding: "12px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 10, color: GM.vellumDim, cursor: "pointer", fontFamily: GM.font, fontSize: 13 }}>
                ← Back to Shop
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── HOLO FULL SCREEN REVEAL ── */}
      {showHoloReveal && (() => {
        // Slot index of the open reveal — keys the instance (fresh dupe
        // check per slot) and routes its scrap back to the right tile.
        const hrIndex = cards.indexOf(showHoloReveal);
        return (
          <HoloReveal
            key={hrIndex}
            cardResult={showHoloReveal}
            uid={playerName}
            dupeScrap={{
              scrappedPay: hrIndex >= 0 ? scrappedAt[hrIndex] : undefined,
              onScrapped: (paid) => { if (hrIndex >= 0) setScrappedAt(s => ({ ...s, [hrIndex]: paid })); },
            }}
            onClose={() => setShowHoloReveal(null)}
          />
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PACK SHOP WRAPPER — handles the full shop + open flow
// ─────────────────────────────────────────────────────────────
// ── THE LIVE SHELF (2026-08-18) ──────────────────────────────────────
// App hands the Emporium a SNAPSHOT profile taken when the room opens,
// so every number on every shelf froze at the door. That was survivable
// while the only stale value was a coin count nobody re-read mid-visit.
// The Foil Vault's one-per-account seal makes it visible: buy a foil,
// and the tile keeps offering it until you leave and come back.
//
// Same fix as the Forge set-rail staleness — the live listener's FIRST
// fire re-seeds from Firebase, so the snapshot cannot lie past the first
// frame, and the prop is kept as the seed so nothing flashes empty while
// the listener connects. Fixes the coin staleness that was always there
// as a side effect.
function useLiveProfile(seed, uid) {
  const [live, setLive] = useState(seed);
  useEffect(() => { setLive(seed); }, [seed]);
  useEffect(() => {
    if (!uid) return;
    return onValue(ref(db, `profiles/${uid}`), (snap) => {
      const v = snap.val();
      // Keep the uid the caller resolved — the profile node does not
      // carry one, and every shelf below keys its writes off it.
      if (v) setLive({ ...v, uid });
    }, () => { /* an unreadable profile keeps the seed, never blanks */ });
  }, [uid]);
  return live;
}

export function PackShopScreen({ profile: seedProfile, playerName, onBack }) {
  const profile = useLiveProfile(seedProfile, playerName || seedProfile?.uid);
  const [phase, setPhase] = useState("shop");      // shop | opening
  const [selectedPack, setSelectedPack] = useState(null);
  const [openedCards, setOpenedCards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSelectPack = async (pack) => {
    setLoading(true); setError("");
    try {
      const result = await openPack(playerName, pack.id);
      if (!result.success) { setError(result.reason || "Not enough Chocolate Coins."); setLoading(false); return; }
      setSelectedPack(pack);
      setOpenedCards(result.cards);
      setPhase("opening");
    } catch (e) { setError("Something went wrong. Try again."); }
    setLoading(false);
  };

  const handleOpenAnother = () => {
    setPhase("shop");
    setSelectedPack(null);
    setOpenedCards(null);
  };

  if (phase === "opening" && selectedPack && openedCards) {
    return (
      <BoosterPack
        pack={selectedPack}
        cards={openedCards}
        playerName={playerName}
        onClose={handleOpenAnother}
        onOpenAnother={handleOpenAnother}
      />
    );
  }

  return (
    <>
      {loading && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, color: GM.inkGold, fontSize: 18, fontFamily: GM.font, textShadow: textGlow(GM.inkGoldBright) }}>
          ◆ Opening pack...
        </div>
      )}
      {error && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: OBSIDIAN_SLAB, border: `1px solid ${SD.red}`, color: SD.red, padding: "10px 24px", borderRadius: 8, fontSize: 13, zIndex: 500, fontFamily: GM.font, boxShadow: SHADOW_RAISED }}>
          {error}
        </div>
      )}
      <PackShop profile={profile} playerName={playerName} onSelectPack={handleSelectPack} onBack={onBack} />
    </>
  );
}
